import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { flatMap, isInteger, set, isEmpty, cloneDeep, uniqBy, isArray, intersection } from 'lodash';
import { BombiixTagsInput, BombiixTag } from '../../../../Components/BombiixTagsInput';
import { asyncContainer, Typeahead } from 'react-bootstrap-typeahead';
import './CreateProduct.css';
import {
  getSubBrandDetails,
  getLicensorAccess,
  getSubBrandAccess,
  getTypeBasedCategories,
  getLicensorDetails,
  getLicenseDetails,
  getLikenessDetails,
  clearCustomAttribute,
  fetchProductIdentifier,
  fetchProductIdentifierType,
  getMultiCustomAttribute,
  getCustomAttributeFreeText,
  getCatBasedOnPT,
  clearProductTypeProcessMasterData,
  unmountSystemmaster,
  clearLicenseData,
  clearLikenessData,
  clearSubBrandData,
  saveMultiAttribute,
  SaveCustomAttribute,
  getProductTypeProcessDetails,
  getAdditionalVariablesByProduct,
} from '../../../SystemMasters/Store/SystemMasters.action';
import { getAllGradings } from '../../../Library/Pom/Store/Pom.action';
import { fetchColorListLibrary, fetchColorGroupLibrary } from '../../../Library/ColorLibary/store/ColorLibrary.action';
import { fetchSuppliersFactories } from '../../../Library/BomLibrary/store/BomLibrary.action';
import { fetchMaster } from '../../../../store/master-registry';
import {
  addProduct,
  updateProduct,
  duplicateProduct,
  getEditedProductDetail,
  getProductEditRules,
  getDescriptionevelopmentOrder,
  clearDescriptionSearch,
  unmountProductModal,
  getOtherProductTags,
  getCounterValueNumber,
  productChangeGrading,
  checkDamExist,
  deleteProductAllDam,
  getProductDependentData,
  productChangeFactory,
} from '../../store/DevelopmentOrder.action';
import NewSelectMultiSelect, { type NewSelectMultiSelectOption } from '../../../../Components/PIM/common/NewSelectMultiSelect/NewSelectMultiSelect';
import type {
  AdditionalVariable,
  AdditionalVariableOption,
  AdditionalVariableScope,
  AttributeIdCarrier,
  NestedThunkEnvelope,
  ReferenceSupplierRef,
  ThunkEnvelope,
} from './createProduct.interface';
import unwrapAdditionalVariables from './unwrapAdditionalVariables';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { NewRadio } from '../../../../Components/PIM/common/NewRadio';
import { NewInput } from '../../../../Components/PIM/common/NewInput';
import { NewDatePicker } from '../../../../Components/PIM/common/NewDatePicker';
import CommonColorWayAdd from '../../../../Components/CommonColorWay/CommonColorWayAdd';
import {
  createOptions,
  monthsPlaceholder,
  getYears,
  checkArray,
  converDropdownData,
  shortenStringWithTooltipWithBold,
  transportArray,
  CountriesList,
  getCurrentYearLabel,
} from '../../../../Components/helper';
import ImageUploadModal from '../../../../Components/ModalPopup/ImageUploadModal';
import type { ImageUploadData } from '../../../../Components/ModalPopup/ImageUploadModal';
import { getImagesByProductContext, IMAGE_PICKER_EXTENSIONS } from '../../../AssetLibrary/Assets/store/asset.service';
import Loader from '../../../../Components/Utility/Loader/Loader';
import ProductIdentifier from '../../../../Components/ProductInformation/ProductIdentifier';
import ProductComponentModal, { NO_CARRIED_COMPONENTS, ProductComponentList } from '../../../../Components/ProductInformation/ProductComponentModal';
import { hasCarryableComponents } from './duplicateCarryOver';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import AddMultiDropOptComponent from '../../../../Components/MultiCustomAttributeModal/addMultiDropOpt';
import AddCustomDropOptComponent from '../../../../Components/AddCustomAttributeModal/addCustomDropOption';
import ConfirmationPopupComponent from '../../../../Components/ModalPopup/ConfirmationPopup';
import ConfirmButtonPopupComponent from '../../../../Components/ModalPopup/ConfirmButtonPopup';
import { BombiixModal } from '../../../../Components/BombiixModal';
import { PRODUCT_LIST_PATH } from '../../../../hooks/useProductEditNavigation';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import { mergeInUseSuppliers } from './supplierLock';
import { mergeInUseColours } from './colourLock';
import { mergeInUseFactories } from './factoryLock';
import { resolveInUseFactoryNames } from './resolveInUseFactoryNames';
import { lockedInUseSupplierIds } from './lockedInUseSupplierIds';
import { PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG, hasDependentData, type DependentDataFlags } from './productTypeChange';
import { canSubmitProduct, resolveProductLoadPhase } from './productLoadState';
import { pruneOutOfScopeDynamicColumn, pruneOutOfScopeMultiCustomAttribute } from './customAttributeScope';
import { reconcileAdditionalVariable, type AdditionalVariableSelection } from './additionalVariableScope';
import {
  CHANGE_WARNING,
  NO_ACKNOWLEDGED_WARNINGS,
  acknowledgeWarning,
  hasAcknowledgedWarning,
  shouldConfirmGradingScopeChange,
  type AcknowledgedChangeWarnings,
} from './changeWarnings';
import ProductTypeChangeWarning from './ProductTypeChangeWarning';
import {
  IDENTIFIER_AXIS_FIELDS,
  selectionsFromProduct,
  usageFromSavedProduct,
  wouldDeleteIdentifierRows,
  type PendingIdentifierChange,
} from './identifierAxisUsage';
import { fetchProductIdentifierAxisUsage } from '../../ProductIdentifierTSX/services/product-identifier.service';
import type { IdentifierAxisUsage } from '../../ProductIdentifierTSX/types';

const noImage = require('../../../../assets/images/no-image.png') as string;

const AsyncTypeahead = asyncContainer(Typeahead);
const AddMultiDropOpt = AddMultiDropOptComponent as any;
const AddCustomDropOpt = AddCustomDropOptComponent as any;
/*
 * The popup is plain JS, so TS infers every destructured prop as required — cast so the
 * optional ones can be omitted, matching the casts used for the other JS components above.
 */
const ConfirmationPopup = ConfirmationPopupComponent as any;
const ConfirmButtonPopup = ConfirmButtonPopupComponent as any;

/** A supplier / colourway / factory id as stored on the dev order — always a Mongo id string. */
type EntityId = string;

/**
 * A Supplier(s) dropdown entry. `createOptions` spreads the source supplier over the
 * label/value pair, so the underlying `_id` survives alongside them and is what the
 * in-use lock matches on.
 */
interface SupplierOption extends NewSelectMultiSelectOption {
  _id?: EntityId;
}

/** A factory record inside a `SupplierFactoryGroup.factories` array. */
interface Factory {
  _id: EntityId;
  factoryName?: string;
  country?: string;
  isActive?: boolean;
}

/** One supplier's factory list from the BOM library master, keyed by supplier id. */
interface SupplierFactoryGroup {
  supplierId: EntityId;
  factories: Factory[];
}

/** A Factory(s) dropdown entry. `createOptions` spreads the source factory over the option. */
interface FactoryOption extends NewSelectMultiSelectOption {
  _id?: EntityId;
  country?: string;
}

/** A Product Type change awaiting the user's typed confirmation. */
interface PendingProductTypeChange {
  productType: ProductTypeRef;
}

/**
 * A product type as it reaches `setSupplierOption`. The edit endpoint `$lookup`s SupplierTypes
 * without an `$unwind`, so the same value arrives as an array there, as a populated option from
 * the picker, and as a bare id from other callers.
 */
type ProductTypeRef = { _id?: EntityId; value?: EntityId } | EntityId | null | undefined;

/**
 * Reads the product-type id out of any of the shapes above.
 * @param productType the raw product-type value from state or the edit payload
 * @returns the id, or '' when none is resolvable
 */
const readProductTypeId = (productType: ProductTypeRef | ProductTypeRef[]): EntityId => {
  const source = Array.isArray(productType) ? productType[0] : productType;
  if (!source) return '';
  if (typeof source === 'string') return source;
  return source._id ?? source.value ?? '';
};

/** Maximum characters accepted by the Product Description field. */
const PRODUCT_DESCRIPTION_MAX_LENGTH = 200;

/**
 * Full-page Create / Edit Product screen. Reproduces the add-product path of
 * `Components/ProductInformation/ProductModal.js` (isDuplicateProduct === false)
 * with full field, data-fetch, dependent-dropdown, validation and payload parity.
 * When the route carries a `:devOrderId` param the screen runs in EDIT mode
 * (isProductEdit === true): it prefills the existing product and dispatches
 * `updateProduct` on save. Duplicate / reorder branches are omitted.
 */
function CreateProduct() {
  const [licensorAccess, setLicensorAccess] = useState(false);
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [visibleToSupplier, setVisibleToSupplier] = useState(true);
  const productIdentiRef = useRef<any>(null);

  const dispatch = useDispatch<any>();
  const history = useHistory();
  const location = useLocation();
  const { devOrderId } = useParams<{ devOrderId?: string }>();
  const isProductEdit = Boolean(devOrderId);

  /**
   * Path to leave for on Save / Cancel. Callers that open this screen from a module page
   * (BOM, Measurement, Testing, PO, Range Plan, Placeholder...) pass their own location in
   * router state as `returnTo`, so the user lands back where they started instead of on the
   * product list. Falls back to the product list for direct URLs and for the card/table list.
   */
  const returnPath = (location.state as { returnTo?: string } | undefined)?.returnTo || PRODUCT_LIST_PATH;
  const duplicateProductId = new URLSearchParams(window.location.search).get('duplicateId');
  const reducer = useSelector((reducerstate: any) => reducerstate);
  /* react-i18next@11.8's t() returns TFunctionResult (string | object | ...), which is not a
     ReactNode — so rendering it as JSX children fails to type-check. TranslateFn is the shared
     cast declared in src/global.d.ts. */
  const { t: _t } = useTranslation('common');
  const t = _t as unknown as TranslateFn;
  const lengthLabel = useLengthLabel(t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.LENGTH'));
  const { UserDetails, SystemMastersDetails, PomDetails, ColorLibraryDetails, BomLibraryDetails, Measuremets } = reducer;
  const { userInfo } = UserDetails;
  const { GradingList } = PomDetails;
  const { ColorWaysLibList } = ColorLibraryDetails;
  const { ReferenceSupplier, supplierFactories } = BomLibraryDetails;
  const { descProductList, editedDevOrderData } = Measuremets;
  const { licensorList, licenseList, likenessList, sizeRangesMasterList } = SystemMastersDetails;
  const [isLoadings, setIsLoadings] = useState(true);
  /**
   * Whether the edit/duplicate product fetch has settled (resolved OR failed). The form holds back
   * until it has, because some inputs — the description AsyncTypeahead — are uncontrolled and read
   * their value only once, on mount: render them before the prefill lands and they stay blank even
   * though the value is in state. Tracked as "settled" rather than "has data" so a failed fetch
   * still releases the form instead of hanging on the loader.
   */
  const [productFetchSettled, setProductFetchSettled] = useState(false);
  const [productLoadFailed, setProductLoadFailed] = useState(false);
  const [pendingProductTypeChange, setPendingProductTypeChange] = useState<PendingProductTypeChange | null>(null);
  const [productTypeChangeConfirmed, setProductTypeChangeConfirmed] = useState(false);
  /** BM-9758: blocks a Product Type change/clear while any of the product's suppliers is in use downstream. */
  /** BM-9761: instructs the user when a Factory(s) removal attempt included an in-use factory. */
  const [showFactoryInUseModal, setShowFactoryInUseModal] = useState(false);
  const [blockedInUseFactoryIds, setBlockedInUseFactoryIds] = useState<string[]>([]);
  const [blockedInUseSupplierNames, setBlockedInUseSupplierNames] = useState('');
  /* Additional Variables (each with its own option list) matched to the product's brand+productType+cat1+cat2 */
  const [matchedAdditionalVariables, setMatchedAdditionalVariables] = useState<AdditionalVariable[]>([]);
  const [showBrandSubBrandModal, setShowBrandSubBrandModal] = useState(false);
  const [showGradingScopeModal, setShowGradingScopeModal] = useState(false);
  const [pendingGradingScopeChange, setPendingGradingScopeChange] = useState<{ key: string; value: any; name?: any } | null>(null);
  const [damConfirmModal, setDamConfirmModal] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<any>(null);
  const [selectedKeyChange, setSelectedKeyChange] = useState<string | null>(null);
  const [showFactoryModal, setShowFactoryModal] = useState(false);
  const [pendingFactoryChange, setPendingFactoryChange] = useState<any>(null);
  const [pendingBrandSubBrandChange, setPendingBrandSubBrandChange] = useState<any>(null);
  const acknowledgedChangeWarnings = useRef<AcknowledgedChangeWarnings>({ ...NO_ACKNOWLEDGED_WARNINGS });
  const [transportOption, setTransportOption] = useState<any[]>([]);
  const [typeHeadOpen, setTypeheadOpen] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isAddToCluster, setIsAddToCluster] = useState(false);
  const [isProductComponentModal, setIsProductComponentModal] = useState(false);
  const [currentProductData, setCurrentProductData] = useState<any>(null);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [category3Options, setCategory3Options] = useState<any[]>([]);
  const [category4Options, setCategory4Options] = useState<any[]>([]);
  const [productTypeProcessOptions, setProductTypeProcessOptions] = useState<any[]>([]);

  const {
    brandList,
    category1List,
    category2List,
    customAttributeList,
    pcTypeList,
    productStatusList,
    seasonList,
    category3List,
    category4List,
    lengthsList,
    productIdentifierList,
    autoIdMaster,
    identifierType,
    pcpCustomAttribute,
    subBrandList,
    catOptions1,
    catOptions2,
    catOptions3,
    catOptions4,
    productTypeProcessData,
  } = SystemMastersDetails;
  const [croppedImage, setCroppedImage] = useState<any>(noImage);
  /**
   * Set when the user clears the product picture with the delete control. Clearing the preview is
   * not enough on its own: the update endpoint only drops the stored image (and deletes the S3
   * objects) when the payload carries `removeImage: true`, and an absent/empty `image` is read as
   * "unchanged". Tracked explicitly rather than inferred from croppedImage so a product whose
   * preview simply has not loaded can never be mistaken for a deletion.
   */
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [colourwayModal, setColourwayModal] = useState(false);
  /*
   * Mirrors the "Add to Design/Details" tick in the image upload modal. Sent to the create/update
   * endpoints as `isIncludeDesign`, which is what makes the backend copy the saved product image
   * into this product's Design/Details canvas. Cleared whenever the picture is removed, so a tick
   * left over from an earlier upload can never re-publish a design. Mirrors ProductModal.js.
   */
  const [sendDesignDetail, setSendDesignDetail] = useState(false);
  /*
   * Mirrors the "Add to Asset Library" tick in the image upload modal. Sent to the create/update
   * endpoints as `isAddToAssetLibrary`, which is what makes the backend store the uploaded image
   * under the Asset Library prefix and file it as an asset, rather than keeping it on the
   * product's own prefix. Only ever set for a device upload — an image picked from the library is
   * already an asset. Mirrors ProductModal.js.
   */
  const [sendToAssetLibrary, setSendToAssetLibrary] = useState(false);
  const [uploadColorWay, setUploadColorWay] = useState(false);
  const [lengthEnabled, setLengthEnabled] = useState(true);
  const [focused, setFocused] = useState<any>(null);
  const typeaheadConfig = useRef<any>(null);
  const [suggestionLoader, setsuggestionLoader] = useState(false);
  const [state, setState] = useState<any>({
    editedProductData: { productNumberType: false, counterNo: '' },
    brandOptions: [],
    subBrandOptions: [],
    monthOptions: createOptions(monthsPlaceholder, 'name', '_id'),
    yearOptions: createOptions(getYears(), 'name', '_id'),
    category1Options: [],
    category2Options: [],
    sizeOptions: [],
    lengthOptions: [],
    colourOptions: [],
    supplierOptions: [],
    dynamicFields: [],
    isLoading: true,
    isImageUploadModalOpen: false,
    tagsOptions: [],
    isUploadBtnDisable: false,
    descTags: false,
    defaultStatus: null,
    requiredField: [
      { key: 'number', message: 'Product Number required.', isVisible: false },
      { key: 'description', message: 'Product Description required.', isVisible: false },
      { key: 'brandId', message: 'Brand selection required.', isVisible: false },
      { key: 'catOneId', message: 'Category 1 selection required.', isVisible: false },
      { key: 'catTwoId', message: 'Category 2 selection required.', isVisible: false },
      { key: 'supplierType', message: 'Product Type is required.', isVisible: false },
    ],
    keysToCheck: [],
    sizeKeyId: '',
    lengthKeyId: '',
    colourKeyId: '',
    productNumberKeyId: '',
    activeProductIdentifierList: [],
    multiDynamicAttribute: [],
    showMultiAttributePopup: false,
    multiDynamicId: null,
    newMultiDropOptions: [],
    showCustomAttributeOpt: false,
    customAttributeId: null,
    newCustomDropOptions: [],
  });

  const {
    editedProductData,
    brandOptions,
    subBrandOptions,
    category1Options,
    category2Options,
    yearOptions,
    monthOptions,
    sizeOptions,
    lengthOptions,
    colourOptions,
    isImageUploadModalOpen,
    supplierOptions,
    tagsOptions,
    isUploadBtnDisable,
    dynamicFields,
    requiredField,
    descTags,
    activeProductIdentifierList,
    multiDynamicAttribute,
  } = state;

  const resolveSizeRangesSnapshot = useCallback(() => {
    return editedProductData?.sizeRanges ?? [];
  }, [editedProductData?.sizeRanges]);

  const isProductNumberUniversalAutoGenerated = useMemo(() => {
    const universalNumber = autoIdMaster?.find((item: any) => item.refCode === 'Product_Number_Universal' && item.isModuleActive);
    if (universalNumber) {
      return {
        isActive: universalNumber?.isModuleActive,
        counterNo: universalNumber?.counterNo,
        selectedChildProperties: universalNumber?.selectedChildProperties,
      };
    }
    return { isActive: false, counterNo: '', selectedChildProperties: [] };
  }, [autoIdMaster]);

  const isProductNumberBrandAutoGenerated = useMemo(() => {
    const productNumber = autoIdMaster?.find(
      (item: any) => item.refCode === 'Product_Number' && item.isModuleActive && item.brandId?.toString() === editedProductData?.brandId?.toString(),
    );
    if (productNumber) {
      return { isActive: productNumber?.isModuleActive, counterNo: productNumber?.counterNo, selectedChildProperties: productNumber?.selectedChildProperties };
    }
    return { isActive: false, counterNo: '', selectedChildProperties: [] };
  }, [autoIdMaster, editedProductData?.brandId]);

  const isProductNumberTypeEnabled = useMemo(() => {
    return isProductNumberUniversalAutoGenerated.isActive || isProductNumberBrandAutoGenerated.isActive;
  }, [isProductNumberUniversalAutoGenerated.isActive, isProductNumberBrandAutoGenerated.isActive]);

  const hasAnyBrandWithActiveProductNumber = useMemo(() => {
    if (autoIdMaster?.length > 0) {
      return autoIdMaster.some((item: any) => item.refCode === 'Product_Number' && item.isModuleActive && item.brandId);
    }
    return false;
  }, [autoIdMaster]);

  const shouldShowProductNumberTypeDropdown = useMemo(() => {
    return isProductNumberUniversalAutoGenerated.isActive || isProductNumberBrandAutoGenerated.isActive || hasAnyBrandWithActiveProductNumber;
  }, [isProductNumberUniversalAutoGenerated.isActive, isProductNumberBrandAutoGenerated.isActive, hasAnyBrandWithActiveProductNumber]);

  useEffect(() => {
    if (productTypeProcessData) {
      const productTypeData = productTypeProcessData?.map((item: any) => ({
        ...item,
        value: item._id,
        label: item.name,
      }));
      setProductTypeProcessOptions(productTypeData);
    }
  }, [productTypeProcessData]);

  useEffect(() => {
    return () => {
      setProductTypeProcessOptions([]);
      dispatch(clearProductTypeProcessMasterData());
    };
  }, []);

  useEffect(() => {
    const fetchLicensorAccess = async () => {
      const [licensorAccessRes, subBrandAccessRes] = await Promise.all([dispatch(getLicensorAccess()), dispatch(getSubBrandAccess())]);
      setLicensorAccess(licensorAccessRes?.isActiveLicensor || false);
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchLicensorAccess();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getTypeBasedCategories(CATEGORY_TYPES, true));
  }, [dispatch]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo') || 'null');
    if (userData?.lengthVisible !== null && userData?.lengthVisible !== undefined) {
      setLengthEnabled(userData?.lengthVisible);
    }
  }, []);

  useEffect(() => {
    if (editedProductData?.isLicensedProduct) {
      dispatch(getLicensorDetails({ loggedInUserId: userInfo?.userId, isActive: true }));
    }
  }, [editedProductData?.isLicensedProduct, userInfo?.userId, dispatch]);

  useEffect(() => {
    if (editedProductData?.isLicensedProduct && editedProductData?.licensorId) {
      dispatch(getLicenseDetails({ licensorId: editedProductData.licensorId, loggedInUserId: userInfo?.userId, isActive: true }));
    }
  }, [editedProductData?.isLicensedProduct, editedProductData?.licensorId, userInfo?.userId, dispatch]);

  useEffect(() => {
    if (editedProductData?.isLicensedProduct && editedProductData?.licensorId && editedProductData?.licenseId) {
      dispatch(
        getLikenessDetails({
          licensorId: editedProductData.licensorId,
          licenseId: editedProductData.licenseId,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
    }
  }, [editedProductData?.isLicensedProduct, editedProductData?.licensorId, editedProductData?.licenseId, userInfo?.userId, dispatch]);

  /**
   * Factory ids the dev order already consumes (`orderFactoryId` on GET /development-orders/:id,
   * built by `getFactoriesInUseForProduct`). These stay selected but must not be editable from
   * the Factory(s) dropdown. Declared here (ahead of `supplierFactoriesOptions`, which is a
   * `useMemo` evaluated synchronously during render) rather than beside `inUseSupplierIds` /
   * `inUseColourIds` further down, which are only ever read from inside later-defined callbacks.
   */
  const inUseFactoryIds = useMemo(() => {
    const orderFactoryId = editedProductData?.orderFactoryId;
    return new Set<EntityId>(isArray(orderFactoryId) ? orderFactoryId.map((factoryId: EntityId) => String(factoryId)) : []);
  }, [editedProductData?.orderFactoryId]);

  const supplierFactoriesOptions = useMemo<FactoryOption[]>(() => {
    if (!Array.isArray(supplierFactories)) {
      return [];
    }
    const selectedSuppliers: EntityId[] = Array.isArray(editedProductData?.suppliers) ? editedProductData.suppliers : [];
    const typedSupplierFactories = supplierFactories as SupplierFactoryGroup[];
    const filteredSuppFactories = typedSupplierFactories.filter((vl: SupplierFactoryGroup) => selectedSuppliers.includes(vl?.supplierId));
    const groupedFactories: Factory[][] = [];
    filteredSuppFactories.forEach((fac: SupplierFactoryGroup) => groupedFactories.push(fac?.factories || []));
    let factories: Factory[] = flatMap(groupedFactories);
    /*
     * BM-9761: a factory the product already consumes downstream must stay selectable (and
     * therefore visible as a chip) even when its owning supplier is no longer selected —
     * otherwise it silently drops out of the options list the moment the supplier goes, and the
     * value/save-payload filters below would drop it right along with it.
     */
    if (inUseFactoryIds.size > 0) {
      const includedIds = new Set(factories.map((factory: Factory) => factory?._id));
      const allFactories: Factory[] = flatMap(typedSupplierFactories.map((fac: SupplierFactoryGroup) => fac?.factories || []));
      const missingInUseFactories = allFactories.filter((factory: Factory) => inUseFactoryIds.has(String(factory?._id)) && !includedIds.has(factory?._id));
      factories = factories.concat(missingInUseFactories);
    }
    const heldFactoryIds: EntityId[] = Array.isArray(editedProductData?.selectedSupplierFactories) ? editedProductData.selectedSupplierFactories : [];
    factories = factories.filter(
      (factory: Factory) => factory?.isActive !== false || heldFactoryIds.includes(factory?._id) || inUseFactoryIds.has(String(factory?._id)),
    );
    return createOptions(factories, 'factoryName', '_id') as FactoryOption[];
  }, [editedProductData?.suppliers, editedProductData?.selectedSupplierFactories, supplierFactories, inUseFactoryIds]);

  /** Country options for "Countries Sold", keyed by a slug of the country name. */
  const countryOptions = useMemo(() => CountriesList.map((name: string) => ({ label: name, value: name.toLowerCase().replace(/\s+/g, '_') })), []);

  const selectedSoldCountryOptions = useMemo(() => {
    const selected = editedProductData?.soldCountries;
    if (!Array.isArray(selected)) return [];
    const selectedSet = new Set(selected);
    return countryOptions.filter((option: any) => selectedSet.has(option.value));
  }, [countryOptions, editedProductData?.soldCountries]);

  /**
   * Extracts the plain id string from an attribute that may arrive as a string, a select option
   * (`value`), or a populated document (`_id`).
   * @param attribute the raw attribute value held in form state
   * @returns the id as a string, or an empty string when there is no value
   */
  const extractAttributeId = useCallback((attribute: unknown): string => {
    if (!attribute) return '';
    if (typeof attribute === 'string') return attribute;
    if (typeof attribute === 'object') {
      const carrier = attribute as AttributeIdCarrier;
      if (carrier._id) return String(carrier._id);
      if (carrier.value) return String(carrier.value);
      if (carrier.id) return String(carrier.id);
    }
    return String(attribute);
  }, []);

  /**
   * The four product attributes that scope the Additional Variables lookup — brand, Product Type and
   * categories 1 and 2. Joined into a single string so the loader effect below re-runs only when one
   * of them actually changes, not when an unrelated field such as Sub Brand or Category 3 does.
   */
  const productAttrsKey = [
    editedProductData?.brandId,
    editedProductData?.supplierType || editedProductData?.supplierTypeId,
    editedProductData?.catOneId,
    editedProductData?.catTwoId,
  ]
    .map((attribute: unknown) => extractAttributeId(attribute))
    .join('_');

  const loadedVariableScopeRef = useRef('');
  loadedVariableScopeRef.current =
    isProductEdit && String(editedDevOrderData?._id ?? '') === String(devOrderId)
      ? [editedDevOrderData?.brandId, editedDevOrderData?.supplierType, editedDevOrderData?.catOneId, editedDevOrderData?.catTwoId]
          .map((attribute: unknown) => extractAttributeId(attribute))
          .join('_')
      : '';

  /**
   * Resolves the Additional Variables configs whose scope (brand + product type +
   * category1 + category2) matches the current product. Re-runs when any of
   * those four attributes change (via productAttrsKey).
   */
  useEffect(() => {
    let cancelled = false;
    /* Only a successful lookup for a scope the user changed may clear the stored selection: the empty list set while loading,
       and the lookup for the scope the product was saved with, must leave it as stored. */
    const applyMatchedAdditionalVariables = (list: AdditionalVariable[], scopeKey: string) => {
      setMatchedAdditionalVariables(list);
      if (scopeKey === loadedVariableScopeRef.current) return;
      setState(
        (
          prevState: Record<string, unknown> & { editedProductData?: Record<string, unknown> & { additionalVariables?: AdditionalVariableSelection | null } },
        ) => {
          const selection = prevState.editedProductData?.additionalVariables;
          const reconciled = reconcileAdditionalVariable(selection, list);
          if (!selection || reconciled === selection) return prevState;
          return { ...prevState, editedProductData: { ...prevState.editedProductData, additionalVariables: reconciled } };
        },
      );
    };
    const loadAdditionalVariables = async () => {
      const attributes: AdditionalVariableScope = {
        brandId: extractAttributeId(editedProductData?.brandId),
        productType: extractAttributeId(editedProductData?.supplierType) || extractAttributeId(editedProductData?.supplierTypeId),
        category1: extractAttributeId(editedProductData?.catOneId),
        category2: extractAttributeId(editedProductData?.catTwoId),
      };
      const filteredAttributes = Object.fromEntries(Object.entries(attributes).filter(([, value]) => value != null && value !== ''));
      const scopeKey = [attributes.brandId, attributes.productType, attributes.category1, attributes.category2].join('_');
      try {
        if (Object.keys(filteredAttributes).length === 0) {
          if (!cancelled) setMatchedAdditionalVariables([]);
          return;
        }
        const variablesRes: AdditionalVariable[] | NestedThunkEnvelope<AdditionalVariable[]> = await dispatch(
          getAdditionalVariablesByProduct(filteredAttributes),
        );
        const list = unwrapAdditionalVariables(variablesRes);
        if (!cancelled) applyMatchedAdditionalVariables(list, scopeKey);
      } catch (error) {
        if (!cancelled) setMatchedAdditionalVariables([]);
      }
    };
    loadAdditionalVariables();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productAttrsKey]);

  const normalizedSizeRangesMasterList = useMemo(() => {
    if (Array.isArray(sizeRangesMasterList)) return sizeRangesMasterList;
    if (Array.isArray(sizeRangesMasterList?.data)) return sizeRangesMasterList.data;
    return [];
  }, [sizeRangesMasterList]);

  const sizeRangeDropdownOptions = useMemo(() => {
    return (
      normalizedSizeRangesMasterList
        ?.filter((range: any) => !range?.isDeleted && range?.isActive)
        ?.map((range: any) => ({ label: range?.name ?? '', value: range?._id })) ?? []
    );
  }, [normalizedSizeRangesMasterList]);

  const sizeRangeSizeDropdownOptions = useMemo(() => {
    const selectedRangeIds = editedProductData?.sizeRangeId || [];
    if (!Array.isArray(selectedRangeIds) || selectedRangeIds.length === 0) {
      return [];
    }
    const allSizes: any[] = [];
    normalizedSizeRangesMasterList?.forEach((range: any) => {
      if (selectedRangeIds.includes(range?._id) && Array.isArray(range?.sizes)) {
        range.sizes
          ?.filter((size: any) => !size?.isDeleted && size?.isActive)
          ?.forEach((size: any) => {
            allSizes.push({ label: size?.name ?? '', value: size?._id, sizeRangeId: range?._id, sizeRangeName: range?.name ?? '' });
          });
      }
    });
    return allSizes;
  }, [normalizedSizeRangesMasterList, editedProductData?.sizeRangeId]);

  useEffect(() => {
    if (transportArray?.length > 0) {
      const array = transportArray.map((item: any) => ({ label: item.label, value: item.name }));
      setTransportOption(array);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoadings(true);
      try {
        await Promise.allSettled([
          (async () => {
            /*
             * Only a brand-new product narrows the picker to active suppliers. In EDIT
             * mode the full list is fetched so a product that already references a
             * deactivated supplier keeps showing and can still be saved. Forced because
             * both variants share the `ReferenceSupplier` slice, so a cache hit could
             * leave the other variant's list in the store.
             */
            const resMainSupplier: ThunkEnvelope<ReferenceSupplierRef[]> = await dispatch(
              fetchMaster('referenceSupplier', isProductEdit ? undefined : { activeOnly: true }, { force: true }),
            );
            const suppliers = resMainSupplier?.code === 200 ? resMainSupplier?.data : ReferenceSupplier;
            if (Array.isArray(suppliers) && suppliers.length) {
              dispatch(fetchSuppliersFactories(suppliers.map((supplier: ReferenceSupplierRef) => supplier?._id)));
            }
          })(),
          dispatch(clearCustomAttribute()),
          dispatch(fetchMaster('pcTypes', { loggedInUserId: userInfo?.userId, isActive: true, tier: 'Tier1' })),
          dispatch(fetchMaster('seasons', { loggedInUserId: userInfo?.userId, isActive: 1 })),
          dispatch(fetchMaster('brands', { loggedInUserId: userInfo?.userId, isActive: 1 })),
          dispatch(fetchMaster('productStatus', { loggedInUserId: userInfo?.userId, isActive: 1 })),
          dispatch(fetchMaster('category1', { categoryType: 1, loggedInUserId: userInfo?.userId, isActive: true })),
          dispatch(fetchMaster('category2', { categoryType: 2, loggedInUserId: userInfo?.userId, isActive: true })),
          dispatch(getAllGradings({ isDropdawn: true, loggedInUserId: userInfo?.userId })),
          dispatch(fetchColorListLibrary({ loggedInUserId: userInfo?.userId })),
          dispatch(fetchMaster('lengths', { isActive: true, loggedInUserId: userInfo?.userId })),
          dispatch(fetchProductIdentifier(t)),
          dispatch(fetchProductIdentifierType(t)),
          dispatch(getMultiCustomAttribute({ isActive: true })),
          dispatch(fetchMaster('autoIdGenerator')),
          dispatch(fetchMaster('sizeRanges', { loggedInUserId: userInfo?.userId })),
        ]);
      } finally {
        setIsLoadings(false);
        setState((prevData: any) => ({ ...prevData, isLoading: false }));
      }
    };

    init();

    return () => {
      typeaheadConfig?.current?.clear();
      dispatch(clearDescriptionSearch());
      dispatch(clearCustomAttribute());
      dispatch(unmountProductModal());
      dispatch(unmountSystemmaster());
    };
  }, [dispatch]);

  useEffect(() => {
    if (checkArray(productStatusList)) {
      const defaultStatusData = productStatusList?.find((ele: any) => ele?.isDefault === true);
      if (!isEmpty(defaultStatusData)) {
        setState((prevState: any) => ({
          ...prevState,
          editedProductData: {
            ...prevState?.editedProductData,
            /*
             * Never inject the default over an existing product's status — on edit the prefill owns
             * this field, and this effect can land either side of it. Mirrors ProductModal.js.
             */
            status: isProductEdit ? prevState?.editedProductData?.status : prevState?.editedProductData?.status || defaultStatusData,
          },
          defaultStatus: defaultStatusData,
        }));
      }
    }
  }, [productStatusList, isProductEdit]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  /**
   * On mount in EDIT mode, fetch the existing product detail and edit rules.
   * Mirrors ProductModal.js (~889-900). No-op when no `:devOrderId` is present.
   */
  useEffect(() => {
    const fetchEditData = async () => {
      if (isProductEdit && devOrderId) {
        /*
         * Only a load that produced a product may release the loader. Settling in a `finally`
         * released it on failure too, which rendered an empty but editable form: saving from
         * there posted the initial state over the product's stored grading groups and sizes,
         * neither of which this screen has a control for.
         */
        const res = await dispatch(getEditedProductDetail(devOrderId));
        if (res) {
          dispatch(getProductEditRules(devOrderId));
          setProductFetchSettled(true);
        } else {
          setProductLoadFailed(true);
        }
      }
    };
    fetchEditData();
  }, [isProductEdit, devOrderId, dispatch]);

  /**
   * On mount in DUPLICATE mode (?duplicateId present), flag the screen as a duplicate
   * and fetch the source product so the prefill effect fills the form. Save still
   * dispatches `addProduct` because `isProductEdit` stays false on the create route.
   */
  useEffect(() => {
    const fetchDuplicateData = async () => {
      if (duplicateProductId) {
        setIsDuplicate(true);
        const res = await dispatch(getEditedProductDetail(duplicateProductId));
        if (res) {
          setProductFetchSettled(true);
        } else {
          setProductLoadFailed(true);
        }
      }
    };
    fetchDuplicateData();
  }, [duplicateProductId, dispatch]);

  /**
   * Prefill `editedProductData` from the fetched `editedDevOrderData` in EDIT mode.
   * Ports the field mapping of ProductModal.js (~913-986): tags, image -> croppedImage,
   * year parseInt, status default, productTypeProcesses array-normalize, category /
   * brand / subBrand ID normalize, sizeRangeId / sizeRangeSizes arrays,
   * selectedSupplierFactories from supplierFactories and visibleToSupplier.
   */
  useEffect(() => {
    const setData = () => {
      const filterTags = editedDevOrderData?.tags?.filter((n: any) => n);
      if (checkArray(filterTags)) {
        const tagsData = createOptions(editedDevOrderData?.tags, 'name', 'id', true);
        setTags(tagsData);
      } else {
        setTags([]);
      }

      if (isDuplicate) {
        delete editedDevOrderData?.number;
        editedDevOrderData.originalColourLibraries = editedDevOrderData?.colourLibraries;
        editedDevOrderData.originalSuppliers = editedDevOrderData?.suppliers;
      }
      if (editedDevOrderData?.image) {
        setCroppedImage(editedDevOrderData?.image);
        setIsImageRemoved(false);
      }
      if (editedDevOrderData?.year) {
        editedDevOrderData.year = parseInt(editedDevOrderData?.year, 10);
      }
      if (isEmpty(editedDevOrderData?.status)) {
        editedDevOrderData.status = state?.defaultStatus;
      }
      if (editedDevOrderData?.productTypeProcesses) {
        editedDevOrderData.productTypeProcesses = Array.isArray(editedDevOrderData?.productTypeProcesses) ? editedDevOrderData.productTypeProcesses : [];
      }

      const normalized = {
        ...editedDevOrderData,
        suppliers: mergeInUseSuppliers(editedDevOrderData?.suppliers, editedDevOrderData?.orderSupplierId),
        /*
         * Colourways consumed downstream (`orderColors`) render disabled in the Colourway(s)
         * dropdown but were only ticked when they also sat in `colourLibraries` — a colourway
         * attached from a PO line / BOM / Costing / Sample / Design Detail alone showed up
         * disabled AND unticked. See colourLock.ts.
         */
        colourLibraries: mergeInUseColours(editedDevOrderData?.colourLibraries, editedDevOrderData?.orderColors),
        brandId: editedDevOrderData?.brandId?._id || editedDevOrderData?.brandId || null,
        subBrandId: editedDevOrderData?.subBrandId?._id || editedDevOrderData?.subBrandId || null,
        supplierTypeId: editedDevOrderData?.supplierType?._id || editedDevOrderData?.supplierType || null,
        catOneId: editedDevOrderData?.catOneId?._id || editedDevOrderData?.catOneId || null,
        catTwoId: editedDevOrderData?.catTwoId?._id || editedDevOrderData?.catTwoId || null,
        catThreeId: editedDevOrderData?.catThreeId?._id || editedDevOrderData?.catThreeId || null,
        catFourId: editedDevOrderData?.catFourId?._id || editedDevOrderData?.catFourId || null,
        productNumberType: editedDevOrderData?.productNumberType || false,
      };

      setState((prevState: any) => ({
        ...prevState,
        editedProductData: {
          ...normalized,
          selectedSupplierFactories: mergeInUseFactories(editedDevOrderData?.supplierFactories, editedDevOrderData?.orderFactoryId),
          productNumberType: editedDevOrderData?.productNumberType || false,
          counterNo: editedDevOrderData?.counterNo || '',
          sizeRangeId: Array.isArray(editedDevOrderData?.sizeRangeId) ? editedDevOrderData.sizeRangeId : [],
          sizeRangeSizes: Array.isArray(editedDevOrderData?.sizeRangeSizes) ? editedDevOrderData.sizeRangeSizes : [],
          soldCountries: Array.isArray(editedDevOrderData?.soldCountries) ? editedDevOrderData.soldCountries : [],
          additionalVariables: editedDevOrderData?.additionalVariables || null,
        },
        descTags: editedDevOrderData?.tagDescription,
      }));
    };
    if ((isProductEdit || isDuplicate) && editedDevOrderData) {
      setData();
      /*
       * `??` (not `!== undefined`): legacy products can carry an explicit null here, which would
       * otherwise reach the switch as `checked={null}` and render as off. Only a real `false` turns
       * the toggle off; null/undefined fall back to the schema default of true.
       */
      setVisibleToSupplier(editedDevOrderData?.visibleToSupplier ?? true);
    }
  }, [editedDevOrderData, isProductEdit, isDuplicate]);

  /**
   * Size Range is single-select. Legacy products saved under the previous multi-select
   * carry more than one range in `sizeRangeId` (and the matching sizes in `sizeRangeSizes`). On
   * load, collapse `sizeRangeId` to its first range and drop any `sizeRangeSizes` that do not
   * belong to it, so the Size(s) dropdown and the saved payload stay consistent with the single
   * range shown. Runs only while more than one range is present, so it fires once per product load
   * (never on a normal single-range re-selection) and is a no-op once the master list is ready.
   */
  useEffect(() => {
    const rangeIds = editedProductData?.sizeRangeId;
    if (!Array.isArray(rangeIds) || rangeIds.length <= 1 || !normalizedSizeRangesMasterList?.length) {
      return;
    }
    const firstRangeId = rangeIds[0];
    const allowedSizeIds: any[] = [];
    normalizedSizeRangesMasterList.forEach((range: any) => {
      if (range?._id === firstRangeId && Array.isArray(range?.sizes)) {
        range.sizes?.filter((size: any) => !size?.isDeleted && size?.isActive)?.forEach((size: any) => allowedSizeIds.push(size?._id));
      }
    });
    const prunedSizes = Array.isArray(editedProductData?.sizeRangeSizes)
      ? editedProductData.sizeRangeSizes.filter((sizeId: any) => allowedSizeIds.includes(sizeId))
      : [];
    setState((prevState: any) => ({
      ...prevState,
      editedProductData: {
        ...prevState.editedProductData,
        sizeRangeId: [firstRangeId],
        sizeRangeSizes: prunedSizes,
      },
    }));
  }, [editedProductData?.sizeRangeId, editedProductData?.sizeRangeSizes, normalizedSizeRangesMasterList]);

  /** The saved product's brand, unwrapped to a plain id so it is a stable effect dependency. */
  const editedDevOrderBrandId = editedDevOrderData?.brandId?._id || editedDevOrderData?.brandId || null;

  /**
   * Sub-brands are only fetched from the brand `onChange` handler, so opening a saved product for
   * edit / duplicate leaves `subBrandList` empty (or holding another screen's brand) and the saved
   * `subBrandId` matches no option — the Sub Brand dropdown renders blank. Seed the list from the
   * product's own brand once it is known. Mirrors ProductModal.js (~304-315).
   */
  useEffect(() => {
    if ((isProductEdit || isDuplicate) && subBrandAccess && editedDevOrderBrandId) {
      dispatch(getSubBrandDetails({ brandId: editedDevOrderBrandId, loggedInUserId: userInfo?.userId }));
    }
  }, [editedDevOrderBrandId, isProductEdit, isDuplicate, subBrandAccess, userInfo?.userId, dispatch]);

  /**
   * In EDIT mode, once the product's supplierType and brand are known, fetch the
   * custom attributes scoped to that product-type + brand so the dynamic fields
   * (and their saved values) render. Mirrors ProductModal.js (~1039-1050). Keyed on the ids, so a
   * reload of the same product does not fetch them again.
   */
  const editedDevOrderTypeId: string | null = editedDevOrderData?.supplierType?._id || editedDevOrderData?.supplierType || null;
  useEffect(() => {
    if (editedDevOrderData?.supplierType && editedDevOrderData?.brandId) {
      dispatch(
        getCustomAttributeFreeText({
          /* `supplierType` arrives either populated or as a bare ObjectId depending on the endpoint,
             so fall back to the raw value exactly as the `brand` line below already does. With only
             `?._id`, a bare id sent productType=undefined; the API treats brand-without-productType
             as an error case and returns [], so every custom attribute silently disappeared. */
          productType: editedDevOrderData?.supplierType?._id || editedDevOrderData?.supplierType,
          brand: editedDevOrderData?.brandId?._id || editedDevOrderData?.brandId,
        }),
      );
      setSupplierOption(editedDevOrderData?.supplierType);
    }
  }, [editedDevOrderData?._id, editedDevOrderTypeId, editedDevOrderBrandId, dispatch]);

  /**
   * Seed category-3 / category-4 dependent options when the edited product carries
   * category selections. Mirrors ProductModal.js (~1201-1210).
   */
  useEffect(() => {
    if ((isProductEdit || isDuplicate) && !isEmpty(editedDevOrderData)) {
      if (editedDevOrderData?.catOneId && editedDevOrderData?.catTwoId) {
        getCategory3Data(editedDevOrderData?.catOneId, editedDevOrderData?.catTwoId);
      }
      if (editedDevOrderData?.catThreeId) {
        getCategory4Data(editedDevOrderData?.catOneId, editedDevOrderData?.catTwoId, editedDevOrderData?.catThreeId);
      }
    }
  }, [editedDevOrderData, isProductEdit, isDuplicate]);

  useEffect(() => {
    const srcCat1 = Array.isArray(catOptions1) && catOptions1.length ? catOptions1 : category1List;
    const srcCat2 = Array.isArray(catOptions2) && catOptions2.length ? catOptions2 : category2List;
    const srcCat3 = Array.isArray(catOptions3) && catOptions3.length ? catOptions3 : category3List;
    const srcCat4 = Array.isArray(catOptions4) && catOptions4.length ? catOptions4 : category4List;

    const ensureInOptions = (opts: any[], masterList: any[], id: any) => {
      if (!id) return opts;
      const safeOpts = Array.isArray(opts) ? opts : [];
      if (safeOpts.some((o) => (o?._id || o?.id) === id)) return safeOpts;
      const src = Array.isArray(masterList) ? masterList : [];
      const found = src.find((o) => (o?._id || o?.id) === id);
      return found ? [...safeOpts, found] : safeOpts;
    };

    const supplierTypeId = editedProductData?.supplierType?._id || editedProductData?.supplierType?.value || null;
    let c1Options = srcCat1 || [];
    let c2Options = srcCat2 || [];

    if (catBasedOnPTData && supplierTypeId) {
      c1Options = (srcCat1 || []).filter((c1: any) => {
        const pts = c1?.productType || [];
        if (!Array.isArray(pts)) return false;
        return pts?.some((pt: any) => (pt?._id || pt) === supplierTypeId);
      });

      const catOneId = editedProductData?.catOneId;
      if (catOneId) {
        c2Options = (srcCat2 || []).filter((c2: any) => {
          const rel = c2?.categoryOneId || c2?.catOneIds || [];
          const arr = Array.isArray(rel) ? rel : [rel];
          return arr?.some((id: any) => (id?._id || id) === catOneId);
        });
      } else {
        c2Options = [];
      }
    } else if (catBasedOnPTData && !supplierTypeId) {
      c1Options = [];
      c2Options = [];
    } else if (!catBasedOnPTData) {
      c1Options = srcCat1 || [];
      c2Options = srcCat2 || [];
    }

    c1Options = ensureInOptions(c1Options, srcCat1 || category1List, editedProductData?.catOneId);
    c2Options = ensureInOptions(c2Options, srcCat2 || category2List, editedProductData?.catTwoId);

    setState((prevState: any) => ({
      ...prevState,
      brandOptions: brandList,
      subBrandOptions: subBrandList,
      category1Options: c1Options,
      category2Options: c2Options,
      lengthOptions: createOptions(lengthsList, 'name', '_id'),
      colourOptions: createOptions(ColorWaysLibList, 'refName', '_id'),
    }));

    const c1Id = editedProductData?.catOneId;
    const c2Id = editedProductData?.catTwoId;
    if (c1Id && c2Id) {
      let c3Opts = (srcCat3 || []).filter((c3: any) => {
        const c1Rel = c3?.categoryOneId || c3?.catOneIds || [];
        const c2Rel = c3?.categoryTwoId || c3?.catTwoIds || [];
        const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
        const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
        return a1?.some((id: any) => (id?._id || id) === c1Id) && a2?.some((id: any) => (id?._id || id) === c2Id);
      });
      c3Opts = ensureInOptions(c3Opts, srcCat3 || category3List, editedProductData?.catThreeId);
      setCategory3Options(c3Opts);

      const c3Id = editedProductData?.catThreeId;
      if (c3Id) {
        let c4Opts = (srcCat4 || []).filter((c4: any) => {
          const c1Rel = c4?.categoryOneId || c4?.catOneIds || [];
          const c2Rel = c4?.categoryTwoId || c4?.catTwoIds || [];
          const c3Rel = c4?.categoryThreeId || c4?.catThreeIds || [];
          const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
          const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
          const a3 = Array.isArray(c3Rel) ? c3Rel : [c3Rel];
          return (
            a1?.some((id: any) => (id?._id || id) === c1Id) &&
            a2?.some((id: any) => (id?._id || id) === c2Id) &&
            a3?.some((id: any) => (id?._id || id) === c3Id)
          );
        });
        c4Opts = ensureInOptions(c4Opts, srcCat4 || category4List, editedProductData?.catFourId);
        setCategory4Options(c4Opts);
      } else {
        setCategory4Options([]);
      }
    } else {
      setCategory3Options([]);
      setCategory4Options([]);
    }
  }, [
    ColorWaysLibList,
    brandList,
    subBrandList,
    category1List,
    category2List,
    category3List,
    category4List,
    catOptions1,
    catOptions2,
    catOptions3,
    catOptions4,
    lengthsList,
    catBasedOnPTData,
    editedProductData?.supplierType?._id,
    editedProductData?.catOneId,
    editedProductData?.catTwoId,
    editedProductData?.catThreeId,
  ]);

  /**
   * Set when the user changes brand or product type, so the scoped-attribute refetch that follows
   * can drop values for attributes the new pair no longer admits. The same refetch also runs on
   * load and must not prune then, which is why this is a flag and not the effect itself.
   */
  const scopeRefetchRef = useRef(false);

  useEffect(() => {
    const callFun = async () => {
      const dynamicFieldsData = await converDropdownData(customAttributeList);
      setState((prevState: any) => ({ ...prevState, dynamicFields: dynamicFieldsData }));
      if (scopeRefetchRef.current) {
        scopeRefetchRef.current = false;
        const inScope = (dynamicFieldsData || []).map((field: { _id?: unknown }) => field?._id);
        setState((prevState: any) => ({
          ...prevState,
          editedProductData: {
            ...prevState.editedProductData,
            dynamicColumn: pruneOutOfScopeDynamicColumn(prevState.editedProductData?.dynamicColumn, inScope),
          },
        }));
      }

      if (state?.newCustomDropOptions?.length > 0 && state?.customAttributeId) {
        const filterData = dynamicFieldsData?.filter((res: any) => res?._id === state?.customAttributeId && res?.type === 'dropdown');
        const filterNewValuesId = filterData?.[0]?.values?.filter((k: any) => state?.newCustomDropOptions?.includes(k.keyName)).map((re: any) => re?._id) || [];

        if (filterData?.length > 0) {
          if (editedProductData?.dynamicColumn) {
            editedProductData.dynamicColumn
              ?.filter((k: any) => k.colId === state?.customAttributeId)
              ?.forEach((re: any) => {
                re.valId = [...new Set([...re.valId, ...filterNewValuesId])];
              });
          } else {
            editedProductData.dynamicColumn = [{ colId: state?.customAttributeId, valId: filterNewValuesId }];
          }
        }

        setState((prevState: any) => ({ ...prevState, newCustomDropOptions: [], customAttributeId: null }));
      }
    };

    if (Array.isArray(customAttributeList)) {
      callFun();
    }
  }, [customAttributeList]);

  useEffect(() => {
    const callFun = async () => {
      setState((prevState: any) => ({ ...prevState, multiDynamicAttribute: pcpCustomAttribute }));

      if (state?.newMultiDropOptions?.length > 0 && state?.multiDynamicId) {
        const filterData = pcpCustomAttribute.filter((res: any) => res._id === state?.multiDynamicId);
        const filterNewValuesId = filterData?.[0]?.values?.filter((k: any) => state?.newMultiDropOptions.includes(k.keyName)).map((re: any) => re._id) || [];
        if (editedProductData?.multiCustomAttribute) {
          editedProductData?.multiCustomAttribute
            ?.filter((k: any) => k.colId === state?.multiDynamicId)
            ?.forEach((re: any) => {
              re.valId = [...new Set([...re.valId, ...filterNewValuesId])];
            });
        } else {
          editedProductData.multiCustomAttribute = [{ colId: state?.multiDynamicId, valId: filterNewValuesId }];
        }

        setState((prevState: any) => ({ ...prevState, newMultiDropOptions: [], multiDynamicId: null }));
      }
    };
    if (Array.isArray(pcpCustomAttribute)) {
      callFun();
    }
  }, [pcpCustomAttribute]);

  useEffect(() => {
    if (pcTypeList?.length === 1) {
      setState((prevState: any) => ({
        ...prevState,
        editedProductData: { ...prevState.editedProductData, supplierType: pcTypeList[0] },
      }));
      const customattributeObject: any = { productType: pcTypeList[0]?._id };
      if (editedDevOrderData?.supplierType) {
        customattributeObject.isActive = true;
      }
      const isBrandScopedFetchOwner = isProductEdit || isDuplicate || Boolean(duplicateProductId);
      if (!isBrandScopedFetchOwner) dispatch(getCustomAttributeFreeText(customattributeObject));
      setSupplierOption(pcTypeList[0]);
    }
  }, [pcTypeList, editedDevOrderData?.supplierType]);

  const handleSelect = async (val: any, key: string, name?: any, isScopeConfirmed = false) => {
    let selectVal: any;
    if (val?._id) selectVal = val?._id;
    if (typeaheadConfig.current) typeaheadConfig.current.clear();
    const copyState = cloneDeep(editedProductData);
    /*
     * Moving a saved product onto a different sub-brand or category can push it outside a grading
     * group it already carries, which removes that group and the measurement data seeded from it.
     * The warning is shown for any such change; the server decides which groups actually no longer
     * apply, so the matching rule is not duplicated here.
     */
    if (
      !isScopeConfirmed &&
      !hasAcknowledgedWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.GRADING_SCOPE) &&
      shouldConfirmGradingScopeChange(key, val, editedProductData, isProductEdit || isDuplicate)
    ) {
      setPendingGradingScopeChange({ key, value: val, name });
      setShowGradingScopeModal(true);
      return;
    }
    switch (key) {
      case 'brandId':
        {
          const currentBrandId = editedProductData?.brandId?._id || editedProductData?.brandId;
          const newBrandId = val?._id;
          /*
           * Re-picking the brand already on the product destroys nothing, so neither warning is
           * raised for it. The DAM check used to run on any selection, which meant a product
           * carrying assets asked to wipe them even when the brand had not moved.
           */
          const brandChanged = String(currentBrandId ?? '') !== String(newBrandId ?? '');
          /* Changing brand on a saved product invalidates its DAM assets — confirm before wiping them. */
          if (brandChanged && devOrderId && !hasAcknowledgedWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.DAM)) {
            const damCheckRes = await dispatch(checkDamExist(devOrderId));
            if (damCheckRes) {
              setSelectedBrandId(val);
              setSelectedKeyChange('brandId');
              setDamConfirmModal(true);
              return;
            }
          }
          /*
           * Switching brand on a saved product can orphan grading groups that do not belong to the
           * new brand, which also clears its size ranges — confirm first. Mirrors ProductModal.js.
           */
          if (
            brandChanged &&
            (isProductEdit || isDuplicate) &&
            !hasAcknowledgedWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.BRAND_GRADING_GROUPS) &&
            editedProductData?.gradingGroups?.length > 0 &&
            currentBrandId !== newBrandId
          ) {
            const compatibleGradingGroups = editedProductData.gradingGroups.filter((group: any) => {
              const gradingDetail = GradingList?.find((g: any) => g._id === group.value);
              if (!gradingDetail) return false;
              return gradingDetail.brand?.some((b: any) => (b._id || b) === newBrandId);
            });
            if (compatibleGradingGroups.length < editedProductData.gradingGroups.length) {
              setPendingBrandSubBrandChange({ key: 'brandId', value: val, previousValue: editedProductData?.brandId, compatibleGradingGroups });
              setShowBrandSubBrandModal(true);
              return;
            }
          }
          if (val?._id) {
            await dispatch(getSubBrandDetails({ brandId: val._id, loggedInUserId: userInfo?.userId }));
          }
          set(copyState, 'subBrandId', null);
        }
        /* One fetch per brand change. This ran twice with an identical payload — the scoping block
           above held a copy — and the two responses raced to set `dynamicFields`. */
        if (val?._id && editedProductData?.supplierType?._id) {
          scopeRefetchRef.current = true;
          dispatch(getCustomAttributeFreeText({ isActive: true, productType: editedProductData?.supplierType?._id, brand: val._id }));
        } else {
          setState((prev: any) => ({ ...prev, dynamicFields: [] }));
        }
        break;
      case 'subBrandId':
        selectVal = val?._id;
        break;
      case 'catOneId':
        {
          if (catBasedOnPTData) {
            const filteredCategory2Options = (category2List || []).filter((category2Item: any) => {
              const category1Relation = category2Item?.catOneIds || [];
              const category1RelationArray = Array.isArray(category1Relation) ? category1Relation : [category1Relation];
              return category1RelationArray?.some((category1Id: any) => (category1Id?._id || category1Id) === selectVal);
            });
            setState((prevState: any) => ({ ...prevState, category2Options: filteredCategory2Options }));
            if (!filteredCategory2Options?.some((option: any) => option?._id === editedProductData?.catTwoId)) {
              set(copyState, 'catTwoId', null);
            }
          }
          set(copyState, 'catThreeId', null);
          set(copyState, 'catFourId', null);
          setCategory4Options([]);
          getCategory3Data(selectVal, editedProductData?.catTwoId);
          set(copyState, 'productTypeProcesses', []);
          await fetchProductTypeProcesses({ ...copyState, catOneId: selectVal });
        }
        break;
      case 'catTwoId':
        {
          set(copyState, 'catThreeId', null);
          set(copyState, 'catFourId', null);
          getCategory3Data(editedProductData?.catOneId, selectVal);
          set(copyState, 'productTypeProcesses', []);
          await fetchProductTypeProcesses({ ...copyState, catTwoId: selectVal });
        }
        break;
      case 'catThreeId':
        set(copyState, 'catFourId', null);
        getCategory4Data(editedProductData?.catOneId, editedProductData?.catTwoId, selectVal);
        set(copyState, 'productTypeProcesses', []);
        await fetchProductTypeProcesses({ ...copyState, catThreeId: selectVal });
        break;
      case 'legLengths':
        selectVal = val;
        break;
      case 'isLicensedProduct':
        selectVal = val;
        break;
      case 'licensorId':
        selectVal = val?._id;
        if (String(val?._id ?? '') !== String(editedProductData?.licensorId ?? '')) {
          dispatch(clearLicenseData());
        }
        dispatch(clearLikenessData());
        set(copyState, 'licenseId', null);
        set(copyState, 'likenessId', null);
        break;
      case 'licenseId':
        selectVal = val?._id;
        if (String(val?._id ?? '') !== String(editedProductData?.licenseId ?? '')) {
          dispatch(clearLikenessData());
        }
        set(copyState, 'likenessId', null);
        break;
      case 'likenessId':
        selectVal = val?._id;
        break;
      case 'suppliers': {
        /* Suppliers already consumed by a purchase order (`orderSupplierId`) are locked.
        Rather than rejecting the whole edit, the locked ids are carried over and the rest of
        the selection is applied — so Clear All and the Select All toggle strip everything
        except them instead of silently doing nothing. The toast explains why the selection
        did not empty completely. Runs before the factory recalculation below so the retained
        suppliers keep their factories. */
        const nextSuppliers = isArray(val) ? val : [];
        const previousSuppliers = isArray(copyState?.suppliers) ? copyState.suppliers : [];
        const inUseSuppliers = isArray(copyState?.orderSupplierId) ? intersection(copyState.orderSupplierId, previousSuppliers) : [];
        const retainedSuppliers = inUseSuppliers.filter((supplierId: EntityId) => !nextSuppliers.includes(supplierId));
        if (retainedSuppliers.length > 0) {
          toasterMsg(t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SUPPLIER_DELETE_MESSAGE'), 'danger');
        }
        const resolvedSuppliers = previousSuppliers
          .filter((supplierId: EntityId) => nextSuppliers.includes(supplierId) || retainedSuppliers.includes(supplierId))
          .concat(nextSuppliers.filter((supplierId: EntityId) => !previousSuppliers.includes(supplierId)));
        selectVal = resolvedSuppliers;
        if (!Array.isArray(supplierFactories)) {
          break;
        }
        const previousFactories = copyState.selectedSupplierFactories || [];
        const availableFactories = supplierFactories
          .filter((supplier: SupplierFactoryGroup) => resolvedSuppliers.includes(supplier.supplierId))
          .flatMap((supplier: SupplierFactoryGroup) => supplier.factories || [])
          .filter((factory: Factory) => factory?.isActive !== false || previousFactories.includes(factory?._id) || inUseFactoryIds.has(String(factory?._id)));
        const updatedFactories = previousFactories.filter((factoryId: EntityId) => availableFactories.some((factory: any) => factory._id === factoryId));
        const [onlyFactory] = availableFactories;
        if (availableFactories.length === 1 && onlyFactory && !updatedFactories.includes(onlyFactory._id)) {
          updatedFactories.push(onlyFactory._id);
        }
        const retainedInUseFactories = previousFactories.filter(
          (factoryId: EntityId) => inUseFactoryIds.has(String(factoryId)) && !updatedFactories.includes(factoryId),
        );
        if (retainedInUseFactories.length > 0) {
          toasterMsg(t('product-information:FACTORY_LOCK.RETAINED_MESSAGE'), 'danger');
        }
        set(copyState, 'selectedSupplierFactories', updatedFactories.concat(retainedInUseFactories));
        break;
      }
      case 'supplierType': {
        if (devOrderId && !hasAcknowledgedWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.PRODUCT_TYPE)) {
          const dependentData = (await dispatch(getProductDependentData(devOrderId))) as DependentDataFlags | null;
          const isDeselect = catBasedOnPTData && !readProductTypeId(val) && isProductEdit;
          if (hasDependentData(dependentData) || isDeselect) {
            setPendingProductTypeChange({ productType: val });
            return;
          }
        }
        await applyProductTypeChange(val);
        return;
      }
      case 'catFourId': {
        set(copyState, 'productTypeProcesses', []);
        await fetchProductTypeProcesses({ ...copyState, catFourId: selectVal });
        break;
      }
      case 'transport':
        selectVal = val?.value;
        break;
      case 'status':
        selectVal = val;
        break;
      case 'selectedSupplierFactories': {
        const previousFactories = editedProductData?.selectedSupplierFactories || [];
        const nextFactories = val || [];
        const removedFactories = previousFactories.filter((factoryId: any) => !nextFactories?.includes(factoryId));
        const blockedFactories = removedFactories.filter((factoryId: EntityId) => inUseFactoryIds.has(String(factoryId)));
        const removableFactories = removedFactories.filter((factoryId: EntityId) => !blockedFactories.includes(factoryId));
        const retainedNextFactories = blockedFactories.length > 0 ? nextFactories.concat(blockedFactories) : nextFactories;
        if (blockedFactories.length > 0) {
          setBlockedInUseFactoryIds(blockedFactories);
          setShowFactoryInUseModal(true);
        }
        if (isProductEdit && removableFactories.length > 0) {
          setPendingFactoryChange({ newFactories: retainedNextFactories, removedFactories: removableFactories });
          if (blockedFactories.length === 0) {
            setShowFactoryModal(true);
          }
          return;
        }
        selectVal = retainedNextFactories;
        break;
      }
      case 'colourLibraries': {
        /* Colourways already consumed by an order colour (`orderColors`) are locked.
        Rather than rejecting the whole edit, the locked ids are carried over and the rest of
        the selection is applied — so Clear All and the Select All toggle strip everything
        except them instead of silently doing nothing. The toast explains why the selection
        did not empty completely. The dropdown greys those rows out via isOptionDisabled;
        this is the state-level backstop for the paths react-select does not gate. */
        const nextColours = isArray(val) ? val : [];
        const previousColours = isArray(copyState?.colourLibraries) ? copyState.colourLibraries : [];
        const inUseColours = isArray(copyState?.orderColors) ? intersection(copyState.orderColors, previousColours) : [];
        const retainedColours = inUseColours.filter((colourId: any) => !nextColours.includes(colourId));
        if (retainedColours.length > 0) {
          toasterMsg(t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.DELETE_MESSAGE'), 'danger');
        }
        selectVal = previousColours
          .filter((colourId: any) => nextColours.includes(colourId) || retainedColours.includes(colourId))
          .concat(nextColours.filter((colourId: any) => !previousColours.includes(colourId)));
        break;
      }
      case 'multiCustomAttribute': {
        const currentArray = Array.isArray(copyState[key]) ? copyState[key] : [];
        const existingEntry = currentArray.find((item: any) => item.colId === name);
        if (existingEntry) {
          selectVal = currentArray.map((item: any) => {
            if (item.colId === name) {
              return { ...item, valId: val };
            }
            return item;
          });
          selectVal = selectVal.filter((item: any) => item.valId.length > 0);
        } else {
          selectVal = [...currentArray, { colId: name, valId: val }];
        }
        break;
      }
      case 'productTypeProcesses':
        selectVal = val;
        break;
      case 'sizeRangeId': {
        selectVal = Array.isArray(val) ? val : [];
        const previousRangeIds = Array.isArray(editedProductData?.sizeRangeId) ? editedProductData.sizeRangeId : [];
        const currentRangeIds = selectVal;
        const addedRangeIds = currentRangeIds.filter((id: any) => !previousRangeIds.includes(id));
        const removedRangeIds = previousRangeIds.filter((id: any) => !currentRangeIds.includes(id));
        let updatedSizeIds = Array.isArray(editedProductData?.sizeRangeSizes) ? [...editedProductData.sizeRangeSizes] : [];

        if (removedRangeIds.length > 0) {
          const sizesToRemove: any[] = [];
          normalizedSizeRangesMasterList?.forEach((range: any) => {
            if (removedRangeIds.includes(range?._id) && Array.isArray(range?.sizes)) {
              range.sizes
                ?.filter((size: any) => !size?.isDeleted && size?.isActive)
                ?.forEach((size: any) => {
                  sizesToRemove.push(size?._id);
                });
            }
          });
          updatedSizeIds = updatedSizeIds.filter((sizeId: any) => !sizesToRemove.includes(sizeId));
        }

        if (addedRangeIds.length > 0) {
          const sizesToAdd: any[] = [];
          normalizedSizeRangesMasterList?.forEach((range: any) => {
            if (addedRangeIds.includes(range?._id) && Array.isArray(range?.sizes)) {
              range.sizes
                ?.filter((size: any) => !size?.isDeleted && size?.isActive)
                ?.forEach((size: any) => {
                  sizesToAdd.push(size?._id);
                });
            }
          });
          updatedSizeIds = [...new Set([...updatedSizeIds, ...sizesToAdd])];
        }

        set(copyState, 'sizeRangeSizes', updatedSizeIds);
        break;
      }
      case 'sizeRangeSizes':
      case 'soldCountries':
        selectVal = Array.isArray(val) ? val : [];
        break;
      default:
        break;
    }
    set(copyState, `${key}`, selectVal);
    if (IDENTIFIER_AXIS_FIELDS.includes(key) && holdIdentifierChange(copyState, { kind: 'select', key, value: val, name })) {
      return;
    }
    setState((prevState: any) => {
      prevState.requiredField?.forEach((e: any) => {
        if (e.key === key) {
          e.isVisible = false;
        }
      });
      return { ...prevState, editedProductData: copyState, requiredField: prevState.requiredField };
    });
  };

  const addComponentType = (id: any) => {
    setState((prevState: any) => ({ ...prevState, showMultiAttributePopup: true, multiDynamicId: id }));
  };

  const addCustomAttributeType = (id: any) => {
    setState((prevState: any) => ({ ...prevState, showCustomAttributeOpt: true, customAttributeId: id }));
  };

  const setSupplierOption = (selectVal: ProductTypeRef | ProductTypeRef[]) => {
    const productTypeId = readProductTypeId(selectVal);
    const supplierOption = productTypeId
      ? ReferenceSupplier.filter((element: { supplierType?: Array<{ _id?: EntityId } | EntityId> }) =>
          element?.supplierType?.some((type) => String(readProductTypeId(type)) === String(productTypeId)),
        )
      : [];
    setState((prevState: any) => ({ ...prevState, supplierOptions: createOptions(supplierOption, 'name', '_id') }));
  };

  /**
   * Asset-library search backing the upload modal on the CREATE path, where the product has no id
   * yet so it owns no assets. Scopes the results to the product's brand / sub-brand / licensing
   * selections instead, and drops anything that is not a supported raster image.
   * Mirrors ProductModal.js.
   */
  /**
   * Page fetcher handed to ImageUploadModal — returns one page of context-filtered library images.
   * The response is returned unfiltered so the modal can compare the fetched page size against the
   * server total for infinite scroll; the extension filter is applied by the modal itself.
   */
  const fetchProductImages = useCallback(
    async (search: string, page: number, limit: number, allowedImageExtensions?: string) => {
      const brandId = editedProductData?.brandId?._id || editedProductData?.brandId || null;
      const subBrandId = editedProductData?.subBrandId?._id || editedProductData?.subBrandId || null;
      const licensorId = editedProductData?.licensorId || null;
      const licenseId = editedProductData?.licenseId || null;
      const likenessId = editedProductData?.likenessId || null;
      return await getImagesByProductContext({
        search,
        page,
        limit,
        ...(allowedImageExtensions ? { allowedImageExtensions } : {}),
        ...(brandId ? { brandIds: [String(brandId)] } : {}),
        subBrandId: subBrandId ? String(subBrandId) : null,
        ...(licensorId ? { licensorIds: [String(licensorId)] } : {}),
        ...(licenseId ? { licenseIds: [String(licenseId)] } : {}),
        ...(likenessId ? { likenessIds: [String(likenessId)] } : {}),
      });
    },
    [editedProductData?.brandId, editedProductData?.subBrandId, editedProductData?.licensorId, editedProductData?.licenseId, editedProductData?.likenessId],
  );

  const showImageUpload = () => {
    setState((prevData: any) => ({ ...prevData, isImageUploadModalOpen: true }));
  };

  /**
   * Dismiss the image upload modal without altering the product image. Committing a selection is
   * handled separately by `handleNewImageUploadSave`.
   */
  const closeImageUpload = () => {
    setState((prevData: any) => ({ ...prevData, isImageUploadModalOpen: false }));
  };

  /**
   * Commit the upload modal's selection onto the product. An asset picked from the library is
   * stored by its S3 key with `isUploadAsset` set, so the backend links the existing asset instead
   * of re-uploading; a file dropped into the modal is stored as a base64 data URL exactly as the
   * old dropzone did. `addToDesign` carries the modal's design tick onto `isIncludeDesign` at save.
   * Mirrors ProductModal.js.
   */
  const handleNewImageUploadSave = ({ file, assetUrl, assetKey, assetId, addToDesign, addToAssetLibrary }: ImageUploadData) => {
    setSendDesignDetail(Boolean(addToDesign));
    setSendToAssetLibrary(Boolean(addToAssetLibrary));
    if (assetKey) {
      const copyState = cloneDeep(editedProductData);
      set(copyState, 'image', assetKey);
      set(copyState, 'fullImg', assetKey);
      set(copyState, 'isUploadAsset', true);
      set(copyState, 'assetId', assetId || null);
      setCroppedImage(assetUrl);
      setState((prevData: any) => ({ ...prevData, isImageUploadModalOpen: false, editedProductData: copyState }));
      return;
    }
    if (!file) {
      setSendDesignDetail(false);
      setSendToAssetLibrary(false);
      setState((prevData: any) => ({ ...prevData, isImageUploadModalOpen: false }));
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const imageRes = reader.result as string;
      const copyState = cloneDeep(editedProductData);
      set(copyState, 'image', imageRes);
      set(copyState, 'fullImg', imageRes);
      set(copyState, 'isUploadAsset', false);
      set(copyState, 'assetId', null);
      if (imageRes?.length) setCroppedImage(imageRes);
      setState((prevData: any) => ({ ...prevData, isImageUploadModalOpen: false, editedProductData: copyState }));
    };
  };

  const isDescriptionTypedRef = useRef(false);
  useEffect(() => {
    if (isDescriptionTypedRef.current && editedProductData?.description?.length > 0) {
      const delayDebounceFn = setTimeout(async () => {
        setsuggestionLoader(true);
        await dispatch(getDescriptionevelopmentOrder({ searchText: editedProductData?.description }));
        setsuggestionLoader(false);
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    }
    return undefined;
  }, [editedProductData?.description]);

  const handleInput = useCallback(
    (text: any, key: string) => {
      const copyState = cloneDeep(editedProductData);
      set(copyState, `${key}`, text);
      requiredField?.forEach((e: any) => {
        if (e.key === key) {
          e.isVisible = false;
        }
      });
      if (key !== 'description') {
        if (typeaheadConfig.current) typeaheadConfig.current.clear();
      }
      setState((prevState: any) => ({ ...prevState, editedProductData: copyState, requiredField }));
    },
    [editedProductData, requiredField],
  );

  useEffect(() => {
    if (!isProductNumberTypeEnabled && editedProductData?.productNumberType === true) {
      handleInput(false, 'productNumberType');
    }
  }, [isProductNumberTypeEnabled, editedProductData?.productNumberType, handleInput]);

  const handleAutoGenerateProductNumber = async () => {
    try {
      if (isProductNumberBrandAutoGenerated?.isActive && !editedProductData?.brandId) {
        toasterMsg('Please select a brand to generate product number', 'warning');
        return;
      }
      const propertiesToGenerateAutoId =
        isProductNumberUniversalAutoGenerated?.selectedChildProperties?.length > 0
          ? isProductNumberUniversalAutoGenerated?.selectedChildProperties
          : isProductNumberBrandAutoGenerated?.selectedChildProperties?.length > 0
          ? isProductNumberBrandAutoGenerated?.selectedChildProperties
          : [];

      let selectedCounterType = null;
      if (propertiesToGenerateAutoId?.length > 0) {
        const propertiesArray = propertiesToGenerateAutoId.map((prop: any) => {
          if (typeof prop === 'object' && prop?.value) {
            return prop.value;
          }
          return prop;
        });
        if (propertiesArray.includes('3')) {
          selectedCounterType = '3';
        } else if (propertiesArray.includes('5') || propertiesArray.includes('counter')) {
          selectedCounterType = '5';
        }
      }
      const defaultCounterValue = selectedCounterType === '3' ? '000' : '00000';
      let getCurrentCounterNo: any = '';
      if (propertiesToGenerateAutoId?.length > 0 && (isProductNumberUniversalAutoGenerated?.isActive || isProductNumberBrandAutoGenerated?.isActive)) {
        if (isProductEdit && !isDuplicate && editedProductData?.counterNo && editedProductData.counterNo !== defaultCounterValue) {
          getCurrentCounterNo = editedProductData.counterNo;
        } else {
          getCurrentCounterNo = await dispatch(getCounterValueNumber(editedProductData?.brandId));
        }
      }

      const brandMap = new Map(brandList?.map((b: any) => [b._id?.toString(), b.aliasName]) || []);
      const subBrandMap = new Map(subBrandList?.map((sb: any) => [sb._id?.toString(), sb.aliasName]) || []);
      const seasonMap = new Map(seasonList?.map((s: any) => [s._id?.toString(), s.aliasName]) || []);
      const monthMap = new Map(monthOptions?.map((m: any) => [m._id?.toString(), m.name]) || []);
      const yearMap = new Map(yearOptions?.map((y: any) => [y._id?.toString(), y.name]) || []);
      const cat1Map = new Map(category1List?.map((c: any) => [c._id?.toString(), c.aliasName]) || []);
      const cat2Map = new Map(category2List?.map((c: any) => [c._id?.toString(), c.aliasName]) || []);
      const cat3Map = new Map((category3Options || category3List)?.map((c: any) => [c._id?.toString(), c.aliasName]) || []);
      const cat4Map = new Map((category4Options || category4List)?.map((c: any) => [c._id?.toString(), c.aliasName]) || []);
      const productTypeMap = new Map(pcTypeList?.map((pt: any) => [pt._id?.toString(), pt.aliasName || pt.name]) || []);
      const colorWayMap = new Map(ColorWaysLibList?.map((cw: any) => [cw._id?.toString(), cw]) || []);
      const dynamicFieldsMap = new Map(editedProductData?.dynamicColumn?.map((df: any) => [df._id?.toString(), df]) || []);
      const multiCustomAttributeMap = new Map(multiDynamicAttribute?.map((mca: any) => [mca._id?.toString(), mca]) || []);
      const multiCustomAttributeSelectedMap = new Map(editedProductData?.multiCustomAttribute?.map((mca: any) => [mca.colId?.toString(), mca.valId]) || []);
      const resolveProperty = (prop: any) => {
        if (typeof prop === 'object' && prop?.value) {
          prop = prop.value;
        }
        if (prop === '-') return '-';
        if (['3', '5', 'counter'].includes(prop)) return getCurrentCounterNo || null;
        const propertyResolvers: any = {
          brand: () => brandMap.get((selectedBrandId?._id || selectedBrandId || editedProductData?.brandId)?.toString()),
          subBrand: () => subBrandMap.get(editedProductData?.subBrandId?.toString()),
          season: () => seasonMap.get(editedProductData?.seasonId?.toString()),
          month: () => monthMap.get(editedProductData?.month?.toString()),
          year: () => yearMap.get(editedProductData?.year?.toString()),
          category1: () => cat1Map.get(editedProductData?.catOneId?.toString()),
          category2: () => cat2Map.get(editedProductData?.catTwoId?.toString()),
          category3: () => cat3Map.get(editedProductData?.catThreeId?.toString()),
          category4: () => cat4Map.get(editedProductData?.catFourId?.toString()),
          productType: () =>
            editedProductData?.supplierType?.aliasName ||
            editedProductData?.supplierType?.name ||
            productTypeMap.get((editedProductData?.supplierType?._id || editedProductData?.supplierType)?.toString()),
          colourway: () => {
            const selectedColorways = editedProductData?.colourLibraries || [];
            if (selectedColorways.length === 1) {
              const colorway: any = colorWayMap.get(selectedColorways[0]?.toString());
              return colorway?.newReferences || null;
            }
            return null;
          },
        };

        if (propertyResolvers[prop]) {
          return propertyResolvers[prop]();
        }

        const dynamicField: any = dynamicFieldsMap.get(prop?.toString());
        if (dynamicField?.selectedOpt?.aliasName) {
          return dynamicField?.selectedOpt?.aliasName;
        }
        if (dynamicField && !dynamicField.selectedOpt?.aliasName) {
          return null;
        }

        const multiCustomAttr: any = multiCustomAttributeMap.get(prop?.toString());
        if (multiCustomAttr) {
          const selectedValIds: any = multiCustomAttributeSelectedMap.get(prop?.toString());
          if (!selectedValIds || selectedValIds.length === 0) {
            return null;
          }
          const aliasNames = selectedValIds
            .map((valId: any) => {
              const value = multiCustomAttr.values?.find((v: any) => v._id?.toString() === valId?.toString());
              return value?.aliasName !== undefined && value?.aliasName !== null ? value.aliasName : null;
            })
            .filter((nameVal: any) => nameVal !== null && nameVal !== '');
          return aliasNames.length > 0 ? aliasNames.join('') : null;
        }
        return null;
      };

      const resolvedParts = propertiesToGenerateAutoId?.map((prop: any) => ({
        value: resolveProperty(prop),
        isDash: prop === '-',
      }));
      const parts: any[] = [];
      for (let i = 0; i < resolvedParts.length; i++) {
        const current = resolvedParts[i];
        if (current.isDash) {
          const hasPreviousValue = parts.length > 0;
          const hasNextValue = resolvedParts.slice(i + 1).some((part: any) => part.value && !part.isDash);
          if (hasPreviousValue && hasNextValue) {
            parts.push('-');
          }
        } else if (current.value) {
          parts.push(current.value);
        }
      }

      const generatedNumber = parts.join('');
      if (generatedNumber) {
        const copyState = cloneDeep(editedProductData);
        set(copyState, 'number', generatedNumber);
        set(copyState, 'counterNo', getCurrentCounterNo);
        requiredField?.forEach((e: any) => {
          if (e.key === 'number') {
            e.isVisible = false;
          }
        });
        setState((prevState: any) => ({ ...prevState, editedProductData: copyState, requiredField }));
        toasterMsg(`${t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER')} generated successfully`, 'success');
      } else {
        toasterMsg('Unable to generate product number. Please fill in required fields.', 'warning');
      }
    } catch (error) {
      toasterMsg('Error generating product number', 'error');
    }
  };

  const getCategory3Data = (cat1: any, cat2: any) => {
    const srcCat3 = Array.isArray(catOptions3) && catOptions3.length ? catOptions3 : category3List;
    if (cat1 && cat2) {
      const c3Opts = (srcCat3 || []).filter((c3: any) => {
        const c1Rel = c3?.categoryOneId || c3?.catOneIds || [];
        const c2Rel = c3?.categoryTwoId || c3?.catTwoIds || [];
        const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
        const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
        return a1?.some((id: any) => (id?._id || id) === cat1) && a2?.some((id: any) => (id?._id || id) === cat2);
      });
      setCategory3Options(c3Opts);
    } else {
      setCategory3Options([]);
    }
  };

  const getCategory4Data = (cat1: any, cat2: any, cat3: any) => {
    const srcCat4 = Array.isArray(catOptions4) && catOptions4.length ? catOptions4 : category4List;
    if (cat1 && cat2 && cat3) {
      const c4Opts = (srcCat4 || []).filter((c4: any) => {
        const c1Rel = c4?.categoryOneId || c4?.catOneIds || [];
        const c2Rel = c4?.categoryTwoId || c4?.catTwoIds || [];
        const c3Rel = c4?.categoryThreeId || c4?.catThreeIds || [];
        const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
        const a2 = Array.isArray(c2Rel) ? c2Rel : [c2Rel];
        const a3 = Array.isArray(c3Rel) ? c3Rel : [c3Rel];
        return (
          a1?.some((id: any) => (id?._id || id) === cat1) && a2?.some((id: any) => (id?._id || id) === cat2) && a3?.some((id: any) => (id?._id || id) === cat3)
        );
      });
      setCategory4Options(c4Opts);
    } else {
      setCategory4Options([]);
    }
  };

  const fetchProductTypeProcesses = async (dataForFetch: any) => {
    try {
      const ptId = dataForFetch?.supplierType?._id || dataForFetch?.supplierType?.value || null;
      const c1Id = dataForFetch?.catOneId || null;
      const c2Id = dataForFetch?.catTwoId || null;
      const c3Id = dataForFetch?.catThreeId || null;
      const c4Id = dataForFetch?.catFourId || null;
      setState((prev: any) => ({
        ...prev,
        editedProductData: { ...prev.editedProductData, productTypeProcesses: [] },
      }));
      if (!ptId) {
        setProductTypeProcessOptions([]);
        return;
      }
      await dispatch(
        getProductTypeProcessDetails({
          loggedInUserId: userInfo?.userId,
          isActive: 1,
          productTypeId: ptId,
          categoryOneId: c1Id,
          categoryTwoId: c2Id,
          categoryThreeId: c3Id,
          categoryFourId: c4Id,
        }),
      );
    } catch (e) {
      setProductTypeProcessOptions([]);
    }
  };

  /**
   * Resolve the attribute id from a saved dynamicColumn entry, tolerating the
   * different shapes returned by the API (`_id`, populated `colId._id`, or a raw
   * string `colId`). Mirrors ProductModal.js so edit-mode values bind correctly.
   */
  const getEntryAttrId = (entry: any) => {
    if (!entry) return null;
    if (entry._id) return String(entry._id);
    if (entry.colId && entry.colId._id) return String(entry.colId._id);
    if (typeof entry.colId === 'string') return entry.colId;
    return null;
  };

  const handleSelectDynamic = (valueText: any, key: string, dynamicId: any) => {
    const copyState = cloneDeep(editedProductData);
    const dynamicArray: any[] = [];
    if (!isEmpty(dynamicFields)) {
      dynamicFields.forEach((ele: any) => {
        if (ele._id === dynamicId) {
          const existing = checkArray(copyState?.dynamicColumn) ? copyState.dynamicColumn.find((e: any) => getEntryAttrId(e) === ele._id) : null;
          const base = existing ? { ...ele, ...existing, _id: ele._id } : { ...ele };
          const updatedEle = { ...base, [`${key}`]: valueText };
          dynamicArray.push(updatedEle);
        } else if (checkArray(copyState?.dynamicColumn)) {
          const findOldData = copyState?.dynamicColumn.find((e: any) => getEntryAttrId(e) === ele._id);
          if (!isEmpty(findOldData)) {
            dynamicArray.push({ ...findOldData, _id: ele._id });
          } else {
            dynamicArray.push(ele);
          }
        } else {
          dynamicArray.push(ele);
        }
        if (typeaheadConfig.current) typeaheadConfig.current.clear();
      });
    }
    set(copyState, 'dynamicColumn', dynamicArray);
    setState((prevState: any) => ({ ...prevState, editedProductData: copyState }));
    setFocused(null);
  };

  /**
   * Set the selected options for the product's single Additional Variable. Only one variable can
   * match a product (scopes are unique), so the selection is stored as a single
   * { variableId, optionId[] } object that persists on save and rehydrates on edit/duplicate.
   * @param variableId - Additional Variable id (its own master record)
   * @param optionIds - Chosen option ids; empty when every option is cleared
   */
  const handleAdditionalVariableValueChange = (variableId: string, optionIds: string[]) => {
    const copyState = cloneDeep(editedProductData);
    /**
     * Clearing every option clears the whole selection: a variable with no options selected
     * contributes no combination axis, so storing it would leave an empty column on the grid.
     */
    const updated = optionIds.length ? { variableId, optionId: optionIds } : null;
    set(copyState, 'additionalVariables', updated);
    if (holdIdentifierChange(copyState, { kind: 'variable', variableId, optionIds })) {
      return;
    }
    setState((prevState: any) => ({ ...prevState, editedProductData: copyState }));
  };

  /**
   * Read the option ids selected for the given Additional Variable from the product state.
   * `optionId` is a combination axis stored as an array; a pre-array document holds a bare id,
   * so both shapes are accepted.
   * @param {string} variableId - Additional Variable id
   * @returns {string[]} the selected option ids, or an empty array when there are none
   */
  const getAdditionalVariableValue = (variableId: string): string[] => {
    const selection = editedProductData?.additionalVariables;
    if (selection && String(selection.variableId) === String(variableId)) {
      const optionIds = Array.isArray(selection.optionId) ? selection.optionId : [selection.optionId];
      return optionIds.filter(Boolean).map((optionId: unknown) => String(optionId));
    }
    return [];
  };

  const getOtherTags = async () => {
    const tagsData = await dispatch(getOtherProductTags());
    setState((prevData: any) => ({ ...prevData, tagsOptions: createOptions(tagsData, 'name', 'id', true) }));
  };

  const onDelete = useCallback(
    (tagIndex: number) => {
      setTags(tags.filter((_: any, i: number) => i !== tagIndex));
    },
    [tags],
  );

  /**
   * Adds the typed or picked tag. Comma-separated input becomes one tag per
   * entry, trimmed and skipped when already present — same as the table view.
   */
  const onAddition = useCallback(
    (newTag: BombiixTag) => {
      const nextTags = [...tags];
      String(newTag?.name ?? '')
        .split(',')
        .map((name) => name.trim())
        .forEach((name) => {
          if (name && !nextTags.some((tag) => tag?.name === name)) nextTags.push({ id: name, name });
        });
      setTags(nextTags);
    },
    [tags],
  );

  async function isBase64UrlImage(base64String: string) {
    const image = new Image();
    image.src = base64String;
    return new Promise((resolve) => {
      image.onload = function () {
        if (image.height === 0 || image.width === 0) {
          resolve(false);
          return;
        }
        resolve(true);
      };
      image.onerror = () => {
        resolve(false);
      };
    });
  }

  const checkValidation = (data: any) => {
    let isValid = false;
    let validCount = 0;
    const dataValidate = requiredField.map((ele: any) => {
      if (Object.prototype.hasOwnProperty.call(data, ele.key) && !isEmpty(data[ele.key])) {
        validCount += 1;
        return { ...ele, isVisible: false };
      }
      return { ...ele, isVisible: true };
    });

    if (data?.productIdentifier?.length > 1) {
      const keysToCheck = productIdentifierList
        ?.filter((item: any) => item.name === 'Colourway' || item.name === 'Size' || item.name === 'Length')
        .map((item: any) => item._id);

      if (keysToCheck.length === 0) {
        return undefined;
      }

      data?.productIdentifier?.forEach((obj: any) => {
        const combination = keysToCheck.map((key: any) => obj[key]).join('');
        if (combination === '') {
          toasterMsg(t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.COLOUR_WAY_SIZE_LENGTH'), 'danger');
          validCount += 1;
        }
      });
    }

    if (validCount === requiredField.length) {
      isValid = true;
    }

    setState((prevState: any) => ({ ...prevState, requiredField: dataValidate }));
    return isValid;
  };

  useEffect(() => {
    if (productIdentifierList?.length > 0) {
      const staticKeys: any[] = [];
      productIdentifierList?.forEach((item: any) => {
        if (item.name === 'Colourway' || item.name === 'Size' || item.name === 'Length') {
          staticKeys.push(item._id);
        }
      });

      setState((prevData: any) => ({
        ...prevData,
        keysToCheck: staticKeys,
        sizeKeyId: productIdentifierList.find((item: any) => item?.name === 'Size')?._id,
        lengthKeyId: productIdentifierList.find((item: any) => item?.name === 'Length')?._id,
        colourKeyId: productIdentifierList.find((item: any) => item?.name === 'Colourway')?._id,
        productNumberKeyId: productIdentifierList.find((item: any) => item?.name === 'Product number')?._id,
        activeProductIdentifierList: productIdentifierList.filter((item: any) => item?.isActive),
      }));
    }
  }, [productIdentifierList]);

  const generateStringFromPattern = (pattern: any[], data: any, counterValue: any) => {
    const result: any[] = [];
    for (let i = 0; i < pattern.length; i++) {
      const key = pattern[i];
      if (key === '3' || key === '5' || key === '10' || key === '11' || key === '12' || key === '13') {
        result.push(counterValue);
      } else if (key === '-') {
        if (result.length > 0 && result[result.length - 1] !== '-') {
          result.push('-');
        }
      } else if (data[key]) {
        result.push(data[key]);
      }
    }
    while (result.length > 0 && result[result.length - 1] === '-') {
      result.pop();
    }
    return result.join('');
  };

  let parentData: any;
  const getUpdatedCounter = (identifier: any, rowIndex: number, updatedArr: any[]) => {
    const copyData = cloneDeep(identifier);
    const updatedIdentifier = { ...copyData };
    const resolveColourwayLabel = (val: any) => {
      if (!val) return '';
      const opt = (ColorWaysLibList || []).find((ele: any) => ele?._id === val || ele?.newReferences === val || ele?.name === val);
      return opt?.newReferences || opt?.name || val;
    };

    const existingCombinationMatch = editedProductData?.productIndentifier?.find((dbItem: any) =>
      state.keysToCheck?.every((checkKey: any) => identifier[checkKey] === dbItem[checkKey]),
    );

    Object.keys(copyData).forEach((key) => {
      const matchedAutoId = autoIdMaster?.find((item: any) => item?.productIdentifierId === key);
      const data = matchedAutoId?.selectedChildProperties;
      let lastCount: any = 0;
      if (matchedAutoId?.isModuleActive) {
        const hasExistingValue = !!(copyData[key] && copyData[key] !== '');
        const hasExistingValueInDb = !!(existingCombinationMatch && existingCombinationMatch[key] && existingCombinationMatch[key] !== '');
        const shouldSkipIncrement = hasExistingValue || hasExistingValueInDb;

        let counterValue = copyData[key] ? copyData[key] : matchedAutoId?.counterNo;
        if (data?.some((val: any) => ['3', '5', '10', '11', '12', '13'].includes(val)) && !copyData[key]) {
          const keysToCheckData = data.filter((val: any) => ['3', '5', '10', '11', '12', '13'].includes(val));
          const hasOnlyZeros = keysToCheckData?.every(
            (k: any) => matchedAutoId?.latestCounterValues?.[k] === '0'.repeat(matchedAutoId?.latestCounterValues?.[k]?.length),
          );
          const latestValue = hasOnlyZeros
            ? matchedAutoId?.initialCounterValues?.[keysToCheckData[0]]
            : matchedAutoId?.latestCounterValues?.[data.find((val: any) => ['3', '5', '10', '11', '12', '13'].includes(val))];
          if (latestValue) {
            counterValue = latestValue;
          }
        }

        if (counterValue && !shouldSkipIncrement) {
          let newRowIndex = 0;
          for (let i = 0; i < rowIndex; i++) {
            const prevItem = updatedArr[i];
            const prevItemMatch = editedProductData?.productIndentifier?.find((dbItem: any) =>
              state.keysToCheck?.every((checkKey: any) => prevItem[checkKey] === dbItem[checkKey]),
            );
            const prevItemHasValueInDb = !!(prevItemMatch && prevItemMatch[key] && prevItemMatch[key] !== '');
            if (!prevItemHasValueInDb) {
              newRowIndex++;
            }
          }
          counterValue = counterValue.replace(/(\d+)$/, (match: string) => {
            const incrementedValue = (parseInt(match, 10) + newRowIndex).toString().padStart(match.length, '0');
            return incrementedValue.toString();
          });
        } else if (shouldSkipIncrement && (hasExistingValue || hasExistingValueInDb)) {
          counterValue = hasExistingValue ? copyData[key] : hasExistingValueInDb ? existingCombinationMatch[key] : counterValue;
        }
        if (!copyData[key] && copyData[key] === '') {
          const rowContext = {
            ...(parentData || {}),
            size: state?.sizeKeyId ? copyData?.[state?.sizeKeyId] || '' : '',
            colourway: state?.colourKeyId ? resolveColourwayLabel(copyData?.[state?.colourKeyId]) : '',
          };
          updatedIdentifier[key] = generateStringFromPattern(data, rowContext, counterValue);
        }
        if (updatedArr.length === rowIndex + 1) {
          lastCount = counterValue;
          updatedIdentifier.lastCounter = { ...updatedIdentifier.lastCounter, [key]: lastCount };
        }
      } else if (!matchedAutoId?.isModuleActive && editedProductData?.productIndentifier?.length > 0) {
        const excludedKeys = [...state.keysToCheck, 'tabId'];
        const match = editedProductData?.productIndentifier?.find((dbItem: any) => state.keysToCheck.every((k: any) => identifier[k] === dbItem[k]));
        if (match) {
          Object.entries(match).forEach(([k, value]) => {
            if (!excludedKeys.includes(k)) {
              updatedIdentifier[k] = value;
            }
          });
        }
      }
    });

    return updatedIdentifier;
  };

  const firstAlias = (list: any[], id: any) => {
    if (!Array.isArray(list)) {
      return '';
    }
    const matchedItem = list.find((item) => item._id === id);
    return matchedItem ? matchedItem.aliasName : '';
  };

  const combinationData = async () => {
    const sizeRangesSnapshot = resolveSizeRangesSnapshot();
    const sizeArr = sizeRangesSnapshot || [];
    const lengthArr = editedProductData?.legLengths || [];
    const colourArr = editedProductData?.colourLibraries || [];
    const combinedArr: any[] = [];

    parentData = {
      brand: firstAlias(brandList, editedProductData.brandId),
      season: firstAlias(seasonList, editedProductData.seasonId),
      category1: firstAlias(category1List, editedProductData.catOneId),
      category2: firstAlias(category2List, editedProductData.catTwoId),
      category3: firstAlias(category3List, editedProductData.catThreeId),
      category4: firstAlias(category4List, editedProductData.catFourId),
      month: editedProductData.month,
      year: editedProductData.year,
      sizeRanges: sizeRangesSnapshot?.[0],
      colourLibraries: editedProductData?.colourLibraries,
    };

    const buildObj = (values: { colour?: any; size?: any; length?: any }) => {
      const obj: any = {};
      activeProductIdentifierList.forEach((item: any) => {
        switch (item.name) {
          case 'Product number':
            obj[item?._id] = editedProductData?.number;
            break;
          case 'Colourway':
            obj[item?._id] = values.colour ?? '';
            break;
          case 'Size':
            obj[item?._id] = values.size ?? '';
            break;
          case 'Length':
            obj[item?._id] = values.length ?? '';
            break;
          default:
            obj[item?._id] = '';
        }
      });
      return obj;
    };

    if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
      colourArr.forEach((colour: any) => {
        sizeArr.forEach((size: any) => {
          lengthArr.forEach((length: any) => {
            combinedArr.push(buildObj({ colour, size, length }));
          });
        });
      });
    } else if (isArray(colourArr) && colourArr?.length && isArray(lengthArr) && lengthArr?.length) {
      colourArr.forEach((colour: any) => {
        lengthArr.forEach((length: any) => {
          combinedArr.push(buildObj({ colour, length }));
        });
      });
    } else if (isArray(colourArr) && colourArr?.length && isArray(sizeArr) && sizeArr?.length) {
      colourArr.forEach((colour: any) => {
        sizeArr.forEach((size: any) => {
          combinedArr.push(buildObj({ colour, size }));
        });
      });
    } else if (isArray(lengthArr) && lengthArr?.length && isArray(sizeArr) && sizeArr?.length) {
      sizeArr.forEach((size: any) => {
        lengthArr.forEach((length: any) => {
          combinedArr.push(buildObj({ size, length }));
        });
      });
    } else if (isArray(lengthArr) && lengthArr?.length) {
      lengthArr.forEach((length: any) => {
        combinedArr.push(buildObj({ length }));
      });
    } else if (isArray(sizeArr) && sizeArr?.length) {
      sizeArr.forEach((size: any) => {
        combinedArr.push(buildObj({ size }));
      });
    } else if (isArray(colourArr) && colourArr?.length) {
      colourArr.forEach((colour: any) => {
        combinedArr.push(buildObj({ colour }));
      });
    }

    const sizeId = productIdentifierList.find((item: any) => item.name === 'Size')?._id;
    const sizeToGradingGroupsMap: any = {};
    editedProductData?.gradingGroups?.forEach((group: any) => {
      group.sizeRanges?.forEach((size: any) => {
        if (!sizeToGradingGroupsMap[size.name]) {
          sizeToGradingGroupsMap[size.name] = [];
        }
        sizeToGradingGroupsMap[size.name].push(group);
      });
    });

    const expandedCombinations: any[] = [];
    combinedArr.forEach((prod) => {
      const sizeValue = prod[sizeId];
      if (prod?.tabId) {
        expandedCombinations.push({ ...prod, tabId: prod.tabId });
        return;
      }
      const matchingGroups = sizeToGradingGroupsMap[sizeValue] || [];
      if (matchingGroups.length === 0) {
        expandedCombinations.push({ ...prod, tabId: null });
      } else {
        matchingGroups.forEach((group: any) => {
          expandedCombinations.push({ ...prod, tabId: group?.value });
        });
      }
    });

    return expandedCombinations.map((identifier, rowIndex) => getUpdatedCounter(identifier, rowIndex, expandedCombinations));
  };

  /**
   * Determines whether the duplicated product still shares any supplier and any
   * colourway with the original. When both overlap, the original's component
   * statuses are carryable and the user is offered the component checklist.
   */
  const checkDuplicateOrderDiff = () => hasCarryableComponents(editedProductData);

  /**
   * Saves the duplicate, attaching the chosen removeApproval flags and the cluster flag from the
   * Add To/Create Cluster checkbox.
   *
   * Called from the component checklist once it is confirmed, and directly — with every flag
   * false — when the original carries nothing over and the checklist is skipped.
   * @param compData the removeApproval flags to save the duplicate with
   * @param productData the product to duplicate; pass it on the skipped-checklist path, where the
   *   `currentProductData` state set in the same tick has not applied yet
   */
  const onProductComponentSave = async (compData: ProductComponentList, productData?: unknown) => {
    const cloneCurrentDat = { ...((productData ?? currentProductData) as Record<string, unknown>), removeApproval: compData };
    setIsProductComponentModal(false);
    setIsLoadings(true);
    setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: true }));
    const response = await dispatch(duplicateProduct(duplicateProductId, cloneCurrentDat, isAddToCluster, t));
    await dispatch(clearSubBrandData());
    setIsLoadings(false);
    setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: false }));
    if (response === 200) {
      (history as { push: (path: string, state?: unknown) => void }).push(returnPath, { refreshList: true });
    }
  };

  /** Clears the pending DAM-impacting brand change without applying it. */
  const closeDamCheckModal = () => {
    setDamConfirmModal(false);
    setSelectedBrandId(null);
    setSelectedKeyChange(null);
  };

  /**
   * Applies a confirmed brand change that invalidates the product's DAM assets, deleting them
   * server-side. Mirrors ProductModal.js.
   */
  const deleteDamDataAfterProductTypeAndBrandChange = async () => {
    acknowledgedChangeWarnings.current = acknowledgeWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.DAM);
    const copyState = cloneDeep(editedProductData);

    if (selectedKeyChange === 'brandId' && selectedBrandId) {
      set(copyState, 'brandId', selectedBrandId?._id);
      await dispatch(deleteProductAllDam(devOrderId));
    }
    setState((prevState: any) => ({
      ...prevState,
      editedProductData: { ...editedProductData, ...copyState },
    }));
    closeDamCheckModal();
  };

  /** Applies a confirmed factory removal, detaching the removed factories from the product. */
  const confirmFactoryChange = async () => {
    const { newFactories, removedFactories } = pendingFactoryChange || {};
    const copyState = cloneDeep(editedProductData);
    set(copyState, 'selectedSupplierFactories', newFactories);

    await dispatch(productChangeFactory({ productId: editedProductData?._id, factoryId: removedFactories }));

    setState((prev: any) => ({ ...prev, editedProductData: copyState }));
    setShowFactoryModal(false);
    setPendingFactoryChange(null);
  };

  const cancelFactoryChange = () => {
    setShowFactoryModal(false);
    setPendingFactoryChange(null);
  };

  /**
   * Applies a confirmed brand / sub-brand change: keeps only the grading groups valid for the new
   * brand, rebuilds size ranges from those, and resets grading server-side when none survive.
   * Mirrors ProductModal.js `confirmBrandSubBrandChange`.
   */
  const confirmBrandSubBrandChange = async () => {
    if (!pendingBrandSubBrandChange) return;
    acknowledgedChangeWarnings.current = acknowledgeWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.BRAND_GRADING_GROUPS);

    const { key, value, compatibleGradingGroups } = pendingBrandSubBrandChange;
    const copyState = cloneDeep(editedProductData);
    let gradingWillBeEmpty = false;

    if (key === 'brandId' && Array.isArray(compatibleGradingGroups)) {
      set(copyState, 'gradingGroups', compatibleGradingGroups);
      if (compatibleGradingGroups.length === 0) {
        set(copyState, 'sizeRanges', []);
        gradingWillBeEmpty = true;
      } else {
        const remainingSizeNames = compatibleGradingGroups.flatMap((group: any) => {
          const gradingDetail = GradingList?.find((g: any) => g._id === group.value);
          return gradingDetail?.sizeRanges?.map((s: any) => s.name) ?? [];
        });
        set(copyState, 'sizeRanges', [...new Set(remainingSizeNames)]);
      }
    } else {
      set(copyState, 'gradingGroups', []);
      set(copyState, 'sizeRanges', []);
      gradingWillBeEmpty = true;
    }
    set(copyState, 'sizeRangeId', []);
    set(copyState, 'sizeRangeSizes', []);

    let updatedEditRules = null;
    if (gradingWillBeEmpty && isProductEdit && editedProductData?._id) {
      const res = await dispatch(productChangeGrading(editedProductData?._id, 'deleteAll'));
      if (res) updatedEditRules = res;
    }

    if (key === 'brandId') {
      if (value?._id && editedProductData?.supplierType?._id) {
        dispatch(getCustomAttributeFreeText({ isActive: true, productType: editedProductData?.supplierType?._id, brand: value?._id }));
      } else {
        setState((prev: any) => ({ ...prev, dynamicFields: [] }));
      }
      if (value?._id) {
        await dispatch(getSubBrandDetails({ brandId: value._id, loggedInUserId: userInfo?.userId }));
      }
      set(copyState, 'subBrandId', null);
      set(copyState, key, value?._id);
    } else if (key === 'subBrandId') {
      set(copyState, key, value?._id);
    }

    setState((prevState: any) => {
      prevState.requiredField?.forEach((e: any) => {
        if (e.key === key) {
          e.isVisible = false;
        }
      });
      return {
        ...prevState,
        editedProductData: copyState,
        sizeOptions: [],
        requiredField: prevState.requiredField,
        ...(updatedEditRules ? { editRules: updatedEditRules } : {}),
      };
    });

    setShowBrandSubBrandModal(false);
    setPendingBrandSubBrandChange(null);
  };

  const cancelBrandSubBrandChange = () => {
    setShowBrandSubBrandModal(false);
    setPendingBrandSubBrandChange(null);
  };

  /**
   * Applies the held sub-brand or category change once the user accepts that grading groups the new
   * combination no longer covers will be removed along with their measurement data. The removal
   * itself happens server-side when the product is saved.
   */
  const confirmGradingScopeChange = async () => {
    acknowledgedChangeWarnings.current = acknowledgeWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.GRADING_SCOPE);
    const pending = pendingGradingScopeChange;
    setShowGradingScopeModal(false);
    setPendingGradingScopeChange(null);
    if (pending) {
      await handleSelect(pending.value, pending.key, pending.name, true);
    }
  };

  /** Dismisses the grading-scope warning and abandons the change, leaving the field as it was. */
  const cancelGradingScopeChange = () => {
    setShowGradingScopeModal(false);
    setPendingGradingScopeChange(null);
  };

  /**
   * Applies a Product Type de-selection to the form: clears the product type, the whole category
   * chain, suppliers, processes, custom-attribute values and the Variable 2 selection. Local state
   * only; the server removes the dependent data when the product is saved.
   */
  const applyProductTypeDeselect = () => {
    const copyState = cloneDeep(editedProductData);

    set(copyState, 'supplierType', null);
    set(copyState, 'catOneId', null);
    set(copyState, 'catTwoId', null);
    set(copyState, 'catThreeId', null);
    set(copyState, 'catFourId', null);
    set(copyState, 'suppliers', []);
    set(copyState, 'productTypeProcesses', []);
    set(copyState, 'dynamicColumn', []);
    set(copyState, 'multiCustomAttribute', []);
    set(copyState, 'additionalVariables', null);

    dispatch(clearCustomAttribute());

    if (isProductEdit) {
      setProductTypeChangeConfirmed(true);
    }

    setCategory3Options([]);
    setCategory4Options([]);

    setState((prevState: any) => ({
      ...prevState,
      editedProductData: copyState,
      category1Options: [],
      category2Options: [],
      dynamicFields: [],
      sizeOptions: [],
    }));
  };

  const handleAddUpdate = async () => {
    if (Array.isArray(dynamicFields) && Array.isArray(editedProductData?.dynamicColumn)) {
      const overLimitField = dynamicFields.find((ele: any) => {
        if (ele?.type !== 'text' || !ele?.isActive) return false;
        const limit = ele?.textFieldConfig?.charLimit;
        if (!limit) return false;
        const colEntry = editedProductData.dynamicColumn.find((d: any) => getEntryAttrId(d) === String(ele?._id));
        const len = (colEntry?.valueText || '').length;
        return len > limit;
      });
      if (overLimitField) {
        toasterMsg(t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit: overLimitField?.textFieldConfig?.charLimit }), 'danger');
        return;
      }
    }
    const TempArray: any[] = [];
    let gradGroups: any[] = [];
    const imageData = editedProductData?.isUploadAsset
      ? editedProductData?.image
      : (await isBase64UrlImage(editedProductData?.image))
      ? editedProductData?.image
      : '';
    /**
     * The bundled no-image art is the placeholder shown until a product picture is chosen — it is
     * never a picture to save. `croppedImage` still holds it whenever this product has no image (or
     * the cropper was never opened), so posting it verbatim stored a build-artefact path as the
     * product's image, breaking the thumbnail on the next deploy. Same guard the preview uses below.
     */
    const imageToSave = croppedImage && croppedImage !== noImage ? croppedImage : '';
    let productIdentifierActiveData: any[] = [];
    if (identifierType && editedProductData) {
      const productIdentifierData = await combinationData();
      const activeProductIdentifier = productIdentifierList?.filter((ele: any) => ele.isActive === true).map((ele: any) => ele._id);
      productIdentifierActiveData = productIdentifierData.map((obj: any) => {
        const newObj: any = {};
        Object.keys(obj).forEach((key) => {
          if (activeProductIdentifier.includes(key)) {
            newObj[key] = obj[key];
          }
          newObj.tabId = obj.tabId;
          newObj.lastCounter = obj.lastCounter;
        });
        return newObj;
      });
    }

    /**
     * Drop manually-entered identifier rows whose colourway or length is no longer selected
     * on the product, so de-selecting a colour/length does not leave orphan identifier rows.
     * Mirrors ProductModal.js. Only applies to the manual (non-auto) identifier path.
     */
    let manualProductIdentifier = editedProductData?.productIndentifier;
    if (!identifierType && Array.isArray(manualProductIdentifier) && manualProductIdentifier.length > 0) {
      const colourKeyId = productIdentifierList?.find((item: any) => item?.name === 'Colourway')?._id;
      const lengthKeyId = productIdentifierList?.find((item: any) => item?.name === 'Length')?._id;
      const selectedColours = (editedProductData?.colourLibraries || []).map((id: any) => String(id));
      const selectedLengths = (editedProductData?.legLengths || []).map((id: any) => String(id));
      manualProductIdentifier = manualProductIdentifier.filter((row: any) => {
        const colourVal = colourKeyId ? row?.[colourKeyId] : null;
        const lengthVal = lengthKeyId ? row?.[lengthKeyId] : null;
        const colourOk = !colourVal || selectedColours.includes(String(colourVal));
        const lengthOk = !lengthVal || selectedLengths.includes(String(lengthVal));
        return colourOk && lengthOk;
      });
    }

    const tagsArray = tags?.map((tagsEle: any) => {
      if (tagsEle?.name) {
        return tagsEle?.name;
      }
      return undefined;
    });
    if (checkArray(editedProductData?.dynamicColumn)) {
      /*
       * Only genuinely dead entries are dropped. This also filtered against `dynamicFields`, the
       * brand + product-type scoped fetch, which silently deleted the STORED value of any attribute
       * whose scope had since been edited in System Masters: the attribute fell out of the scoped
       * list and its value went with it on the next save, with nothing on screen to show it.
       */
      editedProductData.dynamicColumn = editedProductData?.dynamicColumn?.filter(
        (ele: { isActive?: boolean; isDeleted?: boolean }) => ele.isActive !== false && ele.isDeleted !== true,
      );
    }

    if (checkArray(editedProductData?.gradingGroups)) {
      gradGroups = editedProductData?.gradingGroups?.map((ele: any) => ele.value);
    }
    const obj: any = {
      ...editedProductData,
      gradingGroups: gradGroups,
      image: editedProductData?.isUploadAsset ? imageData : imageData ? croppedImage : '',
      fullImg: imageToSave ? editedProductData?.fullImg : '',
      catOneId: editedProductData?.catOneId?._id || editedProductData?.catOneId || null,
      catTwoId: editedProductData?.catTwoId?._id || editedProductData?.catTwoId || null,
      catThreeId: editedProductData?.catThreeId?._id || editedProductData?.catThreeId || null,
      catFourId: editedProductData?.catFourId?._id || editedProductData?.catFourId || null,
      brandId: editedProductData?.brandId || null,
      seasonId: editedProductData?.seasonId || null,
      month: editedProductData?.month || null,
      year: (isInteger(editedProductData?.year) && editedProductData?.year.toString()) || null,
      productTypeId: editedProductData?.productTypeId || null,
      supplierType: editedProductData?.supplierType?._id || null,
      status: editedProductData?.status?._id || null,
      transport: editedProductData.transport || null,
      removeImage: isImageRemoved,
      isIncludeDesign: sendDesignDetail,
      isAddToAssetLibrary: sendToAssetLibrary && !editedProductData?.isUploadAsset,
      colourLibraries: JSON.stringify(editedProductData?.colourLibraries || []),
      sizeRanges: JSON.stringify(resolveSizeRangesSnapshot()),
      legLengths: JSON.stringify(editedProductData?.legLengths || []),
      followers: JSON.stringify(editedProductData?.suppliers || []),
      suppliers: JSON.stringify(editedProductData?.suppliers || []),
      placeholderQuantity: editedProductData?.placeholderQuantity ? editedProductData?.placeholderQuantity : 0,
      tags: tagsArray,
      isActive: !(isProductEdit || isDuplicate),
      isReorder: isProductEdit ? !!editedProductData?.isReorder : isAddToCluster,
      ...((isProductEdit || isAddToCluster) && {
        parentDevOrderId: editedProductData?.parentDevOrderId ? editedProductData?.parentDevOrderId : devOrderId || duplicateProductId,
        parentDevId: devOrderId || duplicateProductId,
      }),
      productIndentifier: identifierType ? productIdentifierActiveData : manualProductIdentifier,
      isDuplicate: isDuplicate && !isAddToCluster,
      tagDescription: descTags,
      isLicensedProduct: editedProductData?.isLicensedProduct || false,
      counterNo: editedProductData?.counterNo || '00000',
      dynamicColumn: editedProductData?.dynamicColumn?.map((childEle: any) => {
        return {
          colId: getEntryAttrId(childEle),
          valueId: childEle?.selectedOpt?._id ?? null,
          valueText: childEle?.valueText ? childEle.valueText : '',
        };
      }),
      multiCustomAttribute: editedProductData?.multiCustomAttribute,
      supplierFactories: mergeInUseFactories(
        editedProductData?.selectedSupplierFactories
          ? (supplierFactoriesOptions as FactoryOption[])
              ?.filter(
                (val: FactoryOption) =>
                  !isEmpty(editedProductData?.selectedSupplierFactories) && editedProductData?.selectedSupplierFactories.includes(val.value),
              )
              .map((vl: FactoryOption) => vl?._id)
          : [],
        editedProductData?.orderFactoryId,
      ),
      factoryCountry: editedProductData?.selectedSupplierFactories
        ? uniqBy(
            supplierFactoriesOptions
              ?.filter(
                (val: FactoryOption) =>
                  !isEmpty(editedProductData?.selectedSupplierFactories) && editedProductData?.selectedSupplierFactories.includes(val.value),
              )
              .map((vl: any) => vl?.country),
            (e: any) => e,
          )
        : [],
      visibleToSupplier,
      productNumberType: editedProductData?.productNumberType,
      sizeRangeId: Array.isArray(editedProductData?.sizeRangeId) ? editedProductData.sizeRangeId : [],
      sizeRangeSizes: Array.isArray(editedProductData?.sizeRangeSizes) ? editedProductData.sizeRangeSizes : [],
      soldCountries: Array.isArray(editedProductData?.soldCountries) ? editedProductData.soldCountries : [],
      additionalVariables: editedProductData?.additionalVariables || null,
      [PRODUCT_TYPE_CHANGE_CONFIRMED_FLAG]: isProductEdit && productTypeChangeConfirmed,
      isUploadAsset: editedProductData?.isUploadAsset || false,
      assetId: editedProductData?.isUploadAsset ? editedProductData?.assetId || null : null,
    };
    delete obj?.cat3Data;
    delete obj?.cat4Data;
    delete obj?.packingMethod;
    delete obj?.otherPackingMethod;

    if (!obj.isReorder) {
      delete obj?.parentDevOrderId;
    }
    TempArray.push(obj);
    const validateForm = await checkValidation(TempArray[0]);

    if (validateForm) {
      let response = null;
      setIsLoadings(true);
      setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: true }));
      if (isProductEdit) {
        response = await dispatch(updateProduct(devOrderId, TempArray[0], t));
        await dispatch(clearSubBrandData());
        dispatch(clearLikenessData());
        dispatch(clearLicenseData());
      } else if (isDuplicate && duplicateProductId) {
        setCurrentProductData(TempArray[0]);
        if (!checkDuplicateOrderDiff()) {
          await onProductComponentSave(NO_CARRIED_COMPONENTS, TempArray[0]);
          return;
        }
        setIsProductComponentModal(true);
        setIsLoadings(false);
        setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: false }));
        return;
      } else {
        response = await dispatch(addProduct(TempArray[0], t));
        await dispatch(clearSubBrandData());
      }
      if (response === 200) {
        setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: false }));
        (history as { push: (path: string, state?: unknown) => void }).push(returnPath, { refreshList: true });
      } else {
        setState((prevData: any) => ({ ...prevData, isUploadBtnDisable: false }));
      }
      setIsLoadings(false);
    }
  };

  /**
   * The validation message to show for a field, or null when it is valid. Fields that are not in
   * `requiredField` simply never report an error.
   * @param key the field key to look up
   * @returns the message to display, or null
   */
  const hasError = (key: string) => {
    const checkError = requiredField.find((ele: any) => ele.key === key);
    if (checkError?.isVisible) {
      return checkError.message;
    }
    return null;
  };

  const getSelectValue = (options: any[], selectedId: any) => {
    if (!selectedId) return null;
    if (typeof selectedId === 'object' && (selectedId?._id || selectedId?.id)) return selectedId;
    const opts = Array.isArray(options) ? options : [];
    return opts.find((ele) => (ele?._id || ele?.id) === selectedId) || null;
  };

  const showLabel = (data: any, key: string) => {
    switch (key) {
      case 'Length':
        return lengthOptions.find((ele: any) => ele._id === data)?.name;
      case 'Colourway':
        return colourOptions.find((ele: any) => ele._id === data)?.name;
      default:
        return undefined;
    }
  };

  const closeColourModal = () => {
    setColourwayModal(!colourwayModal);
    dispatch(fetchColorListLibrary({ loggedInUserId: userInfo?.userId }));
  };

  const toogleColorImageUploder = () => setUploadColorWay(!uploadColorWay);

  const removeObjectWithId = (arr: any[], id: any) => {
    const objWithIdIndex = arr.findIndex((obj) => obj.id === id);
    if (objWithIdIndex > -1) {
      arr.splice(objWithIdIndex, 1);
    }
    return arr;
  };

  const handleDescAsTags = (isChecked: boolean) => {
    const descriptionText = editedProductData?.description && editedProductData?.description.trim();
    const descriptionTags = descriptionText?.split(' ');

    if (isChecked && checkArray(descriptionTags)) {
      const tagsObjList = createOptions(descriptionTags, 'name', 'id', true);
      const tagsArray = [...tagsObjList, ...tags];
      setTags(tagsArray);
    } else if (checkArray(descriptionTags)) {
      descriptionTags.forEach((ele: any) => {
        const data = removeObjectWithId(tags, ele);
        setTags(data);
      });
    }
    setState((prevData: any) => ({ ...prevData, descTags: isChecked }));
  };

  const onAutoSuggestion = async (selected: any) => {
    isDescriptionTypedRef.current = false;
    const selectedProduct = selected?.[0];
    if (selectedProduct?._id) {
      /*
       * Duplicate flow (mirrors ProductModal.js onAutoSuggestion ~3841): load the
       * picked product's full detail so brand/category/custom attributes etc.
       * auto-fill. The prefill effect below reacts to editedDevOrderData.
       */
      setIsDuplicate(true);
      await dispatch(getEditedProductDetail(selectedProduct._id));
    } else if (selectedProduct?.description) {
      handleInput(selectedProduct.description, 'description');
    }
    setTypeheadOpen(false);
  };

  useEffect(() => {
    if (focused) {
      if (typeaheadConfig.current) typeaheadConfig.current.clear();
    }
  }, [focused]);

  const handleBlur = () => {
    setTypeheadOpen(false);
    if (typeaheadConfig.current) typeaheadConfig.current.clear();
  };

  const handleInputChange = (e: any) => {
    isDescriptionTypedRef.current = true;
    const input = e?.slice(0, PRODUCT_DESCRIPTION_MAX_LENGTH);
    if (input?.trim()?.length > 0) {
      setTypeheadOpen(true);
    } else {
      dispatch(clearDescriptionSearch());
    }
    const copyState = cloneDeep(editedProductData);
    set(copyState, 'description', input);
    requiredField?.forEach((e2: any) => {
      if (e2.key === 'description') {
        e2.isVisible = false;
      }
    });
    setState((prevState: any) => ({ ...prevState, editedProductData: copyState, requiredField }));
  };

  const colorOptionForProductIdentifier = colourOptions.map((e: any) => e);

  /**
   * Supplier ids the dev order already consumes (`orderSupplierId` on
   * GET /development-orders/:id) AND still holds in its own `suppliers` selection.
   * These stay selected but must not be editable from the Supplier(s) dropdown.
   *
   * Both lists have to agree: a Sample, Costing entry or Purchase Order can name a supplier the
   * product no longer lists, and locking on `orderSupplierId` alone disabled a row the product
   * does not hold and blocked a Product Type change for a supplier that is not on the product.
   */
  const inUseSupplierIds = useMemo(
    () => lockedInUseSupplierIds(editedProductData?.orderSupplierId, editedProductData?.suppliers),
    [editedProductData?.orderSupplierId, editedProductData?.suppliers],
  );
  /**
   * Colourway ids the dev order already consumes (`orderColors` on
   * GET /development-orders/:id). These stay selected but must not be editable
   * from the Colourway(s) dropdown.
   */
  const inUseColourIds = useMemo(() => {
    const orderColors = editedProductData?.orderColors;
    return new Set(isArray(orderColors) ? orderColors.map((colourId: any) => String(colourId)) : []);
  }, [editedProductData?.orderColors]);

  const descTypeaheadKey = useMemo(() => {
    if ((isDuplicate || isProductEdit) && editedDevOrderData?._id) {
      return `desc-${editedDevOrderData._id}`;
    }
    return 'desc-new';
  }, [isDuplicate, isProductEdit, editedDevOrderData?._id]);

  /**
   * Seed the default status on a NEW product only. In edit / duplicate the product carries its own
   * saved status, and this list can resolve after the prefill — without the guard it would race the
   * prefill and overwrite the real status with the default. Mirrors ProductModal.js.
   */
  useEffect(() => {
    if (isProductEdit || isDuplicate || duplicateProductId) return;
    if (productStatusList?.length && productStatusList?.find((vl: any) => vl.isDefault)) {
      const isDefaultStatus = productStatusList?.find((vl: any) => vl.isDefault);
      handleSelect(isDefaultStatus, 'status');
    }
  }, [productStatusList, isProductEdit, isDuplicate, duplicateProductId]);

  const saveMultiCustomAttribute = async (data: any, newOptions: any[]) => {
    setState((prevState: any) => ({ ...prevState, newMultiDropOptions: newOptions.map((k) => k.keyName) }));
    const custAttrParams = { keyNames: JSON.stringify(data) };
    await dispatch(saveMultiAttribute(custAttrParams, t));
    await dispatch(getMultiCustomAttribute({ isActive: true }));
    setState((prevState: any) => ({ ...prevState, showMultiAttributePopup: false }));
  };

  const saveCustomAttribute = async (data: any, newcustomOptions: any[]) => {
    setState((prevState: any) => ({ ...prevState, newCustomDropOptions: newcustomOptions.map((k) => k.keyName) }));
    const custAttrParams = { keyNames: JSON.stringify(data), title: 'custom-attribute-master' };
    const lableId = newcustomOptions[0].dropdownId;

    await dispatch(SaveCustomAttribute(custAttrParams, t));

    const res = await dispatch(
      getCustomAttributeFreeText({
        isActive: true,
        productType: state?.editedProductData?.supplierType?._id,
        brand: state?.editedProductData?.brandId,
      }),
    );

    const filteredOptions = res?.filter((item: any) => item.lableId === lableId) || [];

    const updatedDynamicFields = dynamicFields.map((field: any) => {
      if (field._id === state?.customAttributeId) {
        return { ...field, values: [...(field.values || []), ...filteredOptions] };
      }
      return field;
    });

    const updatedDynamicColumn = [...(editedProductData.dynamicColumn || [])];
    const existing = updatedDynamicColumn.find((item: any) => item._id === state?.customAttributeId);
    const lastOption = filteredOptions[filteredOptions.length - 1];

    if (existing) {
      if (!existing.selectedOpt && lastOption) {
        existing.selectedOpt = lastOption;
      }
    } else if (lastOption) {
      updatedDynamicColumn.push({ _id: state?.customAttributeId, selectedOpt: lastOption });
    }

    setState((prevState: any) => ({
      ...prevState,
      dynamicFields: updatedDynamicFields,
      editedProductData: { ...prevState.editedProductData, dynamicColumn: updatedDynamicColumn },
      showCustomAttributeOpt: false,
      customAttributeId: null,
    }));
  };

  const toggelMultiCustomAttribute = () => {
    setState((prevState: any) => ({ ...prevState, showMultiAttributePopup: false, multiDynamicId: null }));
  };

  const toggelCustomAttribute = () => {
    setState((prevState: any) => ({ ...prevState, showCustomAttributeOpt: false, customAttributeId: null }));
  };

  /*
   * `isLoadings` only covers the master-data fetches; the product fetch runs independently, so on
   * edit/duplicate hold the form until that has settled AND its values have been copied into
   * `editedProductData` by the prefill effect — the effect runs a render later than the fetch, and
   * the uncontrolled description input reads its value only on mount.
   */
  const needsProduct = isProductEdit || Boolean(duplicateProductId);
  const productLoadPhase = resolveProductLoadPhase({
    needsProduct,
    productLoaded: productFetchSettled,
    productLoadFailed,
    prefillApplied: Boolean(editedProductData?._id),
  });

  const dismissFactoryInUseModal = () => {
    setShowFactoryInUseModal(false);
    if (isProductEdit && pendingFactoryChange) {
      setShowFactoryModal(true);
    }
  };

  /**
   * Resets the category chain for a newly selected Product Type when categories are Product Type based,
   * and narrows the first category list to the categories that type offers.
   * @param copyState The product being edited, updated in place.
   * @param productTypeId The selected Product Type id, or empty when the type is cleared.
   */
  const resetCategoriesForProductType = (copyState: Record<string, unknown>, productTypeId: EntityId) => {
    if (!catBasedOnPTData) {
      return;
    }
    set(copyState, 'catOneId', null);
    set(copyState, 'catTwoId', null);
    set(copyState, 'catThreeId', null);
    set(copyState, 'catFourId', null);
    if (productTypeId) {
      const categoriesForProductType = (category1List || []).filter(
        (categoryOne: { productType?: ProductTypeRef[] }) =>
          Array.isArray(categoryOne?.productType) && categoryOne.productType.some((productType) => readProductTypeId(productType) === productTypeId),
      );
      setState((prev: Record<string, unknown>) => ({ ...prev, category1Options: categoriesForProductType, category2Options: [] }));
    } else {
      setState((prev: Record<string, unknown>) => ({ ...prev, category1Options: [], category2Options: [] }));
    }
    setCategory3Options([]);
    setCategory4Options([]);
  };

  /**
   * Applies a Product Type selection to the form: type-specific custom attributes are reloaded and the
   * values the new type does not offer are dropped, suppliers, categories and product-type processes are
   * reset, and the selection lands in `editedProductData` exactly as the select handler's tail would land it.
   * Local state only; on an existing product the change is flagged so the server removes the dependent
   * data when the product is saved.
   * @param productType The selected Product Type, or a clear.
   */
  const applyProductTypeSelection = async (productType: ProductTypeRef) => {
    const copyState = cloneDeep(editedProductData);
    const productTypeId = readProductTypeId(productType);
    if (isProductEdit) {
      setProductTypeChangeConfirmed(true);
    }
    if (productTypeId && editedProductData?.brandId) {
      scopeRefetchRef.current = true;
      dispatch(getCustomAttributeFreeText({ isActive: true, productType: productTypeId, brand: editedProductData?.brandId }));
    } else {
      setState((prev: any) => ({ ...prev, dynamicFields: [] }));
      set(copyState, 'dynamicColumn', []);
      set(copyState, 'additionalVariables', null);
    }
    set(
      copyState,
      'multiCustomAttribute',
      pruneOutOfScopeMultiCustomAttribute(copyState.multiCustomAttribute, multiDynamicAttribute, String(productTypeId ?? '')),
    );
    set(copyState, 'suppliers', []);
    setSupplierOption(productType);
    resetCategoriesForProductType(copyState, productTypeId);
    set(copyState, 'productTypeProcesses', []);
    await fetchProductTypeProcesses({ ...copyState, supplierType: productType });
    set(copyState, 'supplierType', productType);
    setState((prevState: any) => {
      prevState.requiredField?.forEach((field: any) => {
        if (field.key === 'supplierType') {
          field.isVisible = false;
        }
      });
      return { ...prevState, editedProductData: copyState, requiredField: prevState.requiredField };
    });
  };

  /**
   * Applies a Product Type change to the form: a selection, or a clear. Clearing the type of an existing
   * product also empties the category chain when categories are Product Type based; on a new product a
   * clear is applied like any other selection.
   * @param productType The selected Product Type, or a clear.
   */
  const applyProductTypeChange = async (productType: ProductTypeRef) => {
    if (readProductTypeId(productType) || !catBasedOnPTData || !isProductEdit) {
      await applyProductTypeSelection(productType);
      return;
    }
    applyProductTypeDeselect();
  };

  /**
   * Applies the confirmed Product Type change to the form. Nothing is deleted here: the change is
   * flagged and the server removes the dependent data when the product is saved.
   */
  const confirmProductTypeChange = async () => {
    if (!pendingProductTypeChange) {
      return;
    }
    acknowledgedChangeWarnings.current = acknowledgeWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.PRODUCT_TYPE);
    const { productType } = pendingProductTypeChange;
    setPendingProductTypeChange(null);
    await applyProductTypeChange(productType);
  };

  const cancelProductTypeChange = () => {
    setPendingProductTypeChange(null);
  };

  /** Which axis values the saved Product Identifier rows use; null until read, or when the read failed. */
  const [identifierAxisUsage, setIdentifierAxisUsage] = useState<IdentifierAxisUsage | null>(null);
  /** An axis change held until the user accepts that it deletes identifier rows. */
  const [pendingIdentifierChange, setPendingIdentifierChange] = useState<PendingIdentifierChange | null>(null);

  /**
   * Reads which axis values the saved Product Identifier rows use, so an edit that would delete
   * rows can be confirmed first (BM-9211). A failed read leaves the usage null; the check then
   * falls back to the saved product's own selections rather than letting the change through.
   */
  useEffect(() => {
    if (!isProductEdit || !devOrderId) return undefined;
    let isCurrent = true;
    fetchProductIdentifierAxisUsage(devOrderId)
      .then((usage) => {
        if (isCurrent && usage) setIdentifierAxisUsage(usage);
      })
      .catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [isProductEdit, devOrderId]);

  /**
   * Holds a Size Range, Size, Colourway, Variable 1 or Variable 2 change that would make the next
   * save delete saved Product Identifier rows, and raises the warning instead of applying it.
   * Accepting the warning once settles it for the rest of the edit session.
   * @param nextProduct The product as it would be with the change applied.
   * @param pending The change, kept so it can be re-applied once confirmed.
   * @returns True when the change was held and must not be applied yet.
   */
  const holdIdentifierChange = (nextProduct: Record<string, unknown>, pending: PendingIdentifierChange): boolean => {
    if (!isProductEdit || hasAcknowledgedWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.PRODUCT_IDENTIFIER)) {
      return false;
    }
    const usage = identifierAxisUsage ?? usageFromSavedProduct(editedDevOrderData);
    if (!wouldDeleteIdentifierRows(usage, selectionsFromProduct(nextProduct), selectionsFromProduct(editedProductData))) {
      return false;
    }
    setPendingIdentifierChange(pending);
    return true;
  };

  /** Applies the held axis change once the user accepts that it deletes Product Identifier data. */
  const confirmIdentifierChange = async () => {
    acknowledgedChangeWarnings.current = acknowledgeWarning(acknowledgedChangeWarnings.current, CHANGE_WARNING.PRODUCT_IDENTIFIER);
    const pending = pendingIdentifierChange;
    setPendingIdentifierChange(null);
    if (pending?.kind === 'select') {
      await handleSelect(pending.value, pending.key, pending.name);
    } else if (pending?.kind === 'variable') {
      handleAdditionalVariableValueChange(pending.variableId, pending.optionIds);
    }
  };

  /** Dismisses the Product Identifier warning and abandons the change, leaving the field as it was. */
  const cancelIdentifierChange = () => {
    setPendingIdentifierChange(null);
  };

  /*
   * Edit/Duplicate open with the product's type and categories already chosen, so the change
   * handlers that call fetchProductTypeProcesses never fire. Loads the matching options directly
   * instead — fetchProductTypeProcesses would also clear the product's saved processes.
   */
  const editedDevOrderCategoryKey = [editedDevOrderData?.catOneId, editedDevOrderData?.catTwoId, editedDevOrderData?.catThreeId, editedDevOrderData?.catFourId]
    .map((category: unknown) => extractAttributeId(category))
    .join('_');
  useEffect(() => {
    const productTypeId = editedDevOrderData?.supplierType?._id || editedDevOrderData?.supplierType;
    if (!(isProductEdit || isDuplicate) || !productTypeId) return;
    dispatch(
      getProductTypeProcessDetails({
        loggedInUserId: userInfo?.userId,
        isActive: 1,
        productTypeId,
        categoryOneId: editedDevOrderData?.catOneId?._id || editedDevOrderData?.catOneId || null,
        categoryTwoId: editedDevOrderData?.catTwoId?._id || editedDevOrderData?.catTwoId || null,
        categoryThreeId: editedDevOrderData?.catThreeId?._id || editedDevOrderData?.catThreeId || null,
        categoryFourId: editedDevOrderData?.catFourId?._id || editedDevOrderData?.catFourId || null,
      }),
    );
  }, [dispatch, userInfo?.userId, isProductEdit, isDuplicate, editedDevOrderData?._id, editedDevOrderTypeId, editedDevOrderCategoryKey]);

  /**
   * Opens the "Supplier Cannot Be Removed" popup naming the locked suppliers the user just tried
   * to remove from the Supplier(s) dropdown.
   * @param {SupplierOption[]} lockedOptions - The locked supplier options the removal hit.
   * @returns {void}
   */
  const showSupplierLockNotice = (lockedOptions: SupplierOption[]) => {
    setBlockedInUseSupplierNames(lockedOptions.map((option) => option.label).join(', '));
  };

  /**
   * Opens the existing "Factory Cannot Be Removed" popup for the locked factories the user just
   * tried to remove from the Factory(s) dropdown. The same removal can also queue a confirm for
   * the unlocked factories (`pendingFactoryChange`); that confirm is held back until this popup
   * is dismissed (`dismissFactoryInUseModal`) so the two never stack.
   * @param {FactoryOption[]} lockedOptions - The locked factory options the removal hit.
   * @returns {void}
   */
  const showFactoryLockNotice = (lockedOptions: FactoryOption[]) => {
    setShowFactoryModal(false);
    setBlockedInUseFactoryIds(lockedOptions.map((option) => String(option._id)));
    setShowFactoryInUseModal(true);
  };

  if (isLoadings || productLoadPhase === 'loading') {
    return <Loader />;
  }

  /*
   * A product that could not be read is never rendered as a blank form: the screen submits
   * `gradingGroups` and `sizeRanges` without offering a control for either, so an editable
   * empty form here overwrites the product's stored grading scope on the next Save.
   */
  if (productLoadPhase === 'failed') {
    return (
      <div className="page-wraper-block create-product-page">
        <div className="d-flex flex-column align-items-center justify-content-center p-5">
          <p className="mb-16">{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_LOAD_FAILED')}</p>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => window.location.reload()}>
            {t('COMMON_LABEL.RETRY')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wraper-block create-product-page">
      <div className="d-flex align-items-center create-product-header">
        <div className="d-flex align-items-center">
          <figure className="create-product-thumb mb-0">
            {croppedImage && croppedImage !== noImage ? (
              /* A saved asset image is held as a bare S3 key, which does not resolve as an <img>
                 src — fall back to the placeholder icon instead of a broken-image glyph. */
              <img src={croppedImage} alt="Product" onError={() => setCroppedImage(noImage)} />
            ) : (
              <i className="icon-group f-28" />
            )}
          </figure>
          {/* Delete + Upload stacked vertically next to the thumb (Figma: 14px wide, gap 12px). */}
          <div className="create-product-thumb-actions mr-3">
            <CustomTooltip placement="bottom" tooltipText={t('COMMON_LABEL.DELETE')}>
              <button
                type="button"
                className="bombiix-icon-container bombiix-icon-container--red"
                onClick={() => {
                  setCroppedImage(noImage);
                  setIsImageRemoved(true);
                  setSendDesignDetail(false);
                }}
                aria-label={t('COMMON_LABEL.DELETE')}
              >
                <i className="bombiix-icon-delete" />
              </button>
            </CustomTooltip>
            <CustomTooltip placement="bottom" tooltipText={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.UPLOAD')}>
              <button
                type="button"
                className="bombiix-icon-container bombiix-icon-container--blue"
                onClick={() => showImageUpload()}
                aria-label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.UPLOAD')}
              >
                <i className="bombiix-icon-upload" />
              </button>
            </CustomTooltip>
          </div>
          <h2 className="f-24 mb-0">
            {isProductEdit
              ? t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.EDIT_PRODUCT')
              : isDuplicate
              ? t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.DUPLICATE_PRODUCT')
              : t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CREATE_PRODUCT')}
          </h2>
          <div className="d-inline-flex align-items-center design-switch ml-4">
            <input
              type="checkbox"
              className="form-check-input mt-0 comment-switch-input"
              id="visibleToSupplierSwitch"
              name="visibleToSupplier"
              checked={visibleToSupplier}
              onChange={(e: any) => setVisibleToSupplier(e.target.checked)}
            />
            <label className="switch-text font-heavy mb-0 ml-2" htmlFor="visibleToSupplierSwitch">
              {t('product-information:PRODUCT_MODAL.VISIBLE_TO_SUPPLIER_LABEL')}
            </label>
          </div>
        </div>
        {/* Save (primary) + Cancel (secondary) grouped at the header's right edge,
            using the masterDesign button pair: .btn-blue primary + .btn-light secondary.
            The Add To/Create Cluster checkbox sits just before Save on a DUPLICATE only — it decides
            whether the copy joins the source's cluster. It is not offered on edit: an existing product's
            membership is fixed and the save object reads it from editedProductData, never from this box,
            so showing it there rendered a dead, unticked control on clustered products (BM-9573). Mirrors
            the table-view inline row's Cluster-Product control. */}
        <div className="d-flex align-items-center ml-auto">
          {isDuplicate && (
            <div className="d-flex align-items-center mr-3">
              <NewCheckbox
                id="addToCluster"
                name="addToCluster"
                label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CLUSTER_PRODUCT')}
                checked={isAddToCluster}
                onChange={(e: any) => setIsAddToCluster(e.target.checked)}
              />
            </div>
          )}
          <button
            type="button"
            className="bombiix-btn btn-blue mr-2"
            disabled={isUploadBtnDisable || !canSubmitProduct(productLoadPhase)}
            onClick={() => handleAddUpdate()}
          >
            {t('COMMON_LABEL.SAVE')}
          </button>
          <button type="button" className="bombiix-btn btn-light" onClick={() => history.push(returnPath)}>
            {t('COMMON_LABEL.CANCEL')}
          </button>
        </div>
      </div>

      <div className="create-product-body">
        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_IDENTITY')}</Card.Header>
          <Card.Body>
            <Row>
              {shouldShowProductNumberTypeDropdown && (
                <Col md="2">
                  <div className="common-input-control">
                    <div className="d-flex align-items-center position-relative">
                      <div className="common-react-select mb-0 w-100">
                        <NewSelectMultiSelect
                          label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_TYPE')}
                          required
                          isDisabled={!isProductNumberTypeEnabled}
                          value={{
                            label: editedProductData?.productNumberType
                              ? t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_AUTO_GENERATED')
                              : t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_MANUAL'),
                            value: editedProductData?.productNumberType,
                          }}
                          onChange={(selected: any) => handleInput(selected?.value ?? false, 'productNumberType')}
                          options={[
                            { label: t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_MANUAL'), value: false },
                            { label: t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_AUTO_GENERATED'), value: true },
                          ]}
                          placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_SELECT_TYPE')}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              )}
              <Col md="2">
                <div className="common-input-control">
                  <div className="d-flex align-items-center position-relative">
                    <NewInput
                      label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER')}
                      required
                      invalid={!!hasError('number')}
                      value={editedProductData?.number || ''}
                      disabled={
                        editedProductData?.productNumberType === true &&
                        (isProductNumberUniversalAutoGenerated.isActive || isProductNumberBrandAutoGenerated.isActive)
                      }
                      placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_PLACEHOLDER')}
                      onChange={(e: any) => handleInput(e.target.value, 'number')}
                      className={editedProductData?.productNumberType === true ? 'pr-4' : 'pr-0'}
                    />
                    {(isProductNumberUniversalAutoGenerated.isActive || isProductNumberBrandAutoGenerated.isActive) &&
                      editedProductData?.productNumberType === true && (
                        <i
                          className="icon-Bulk_Update f-24 auto-id-refresh-icon"
                          onClick={handleAutoGenerateProductNumber}
                          title={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_NUMBER_REFRESH_TOOLTIP')}
                        />
                      )}
                  </div>
                  <span className="error-message d-block">{hasError('number')}</span>
                </div>
              </Col>
              <Col md="2">
                <div className="common-input-control">
                  {!isProductEdit && (
                    <label className="bombiix-input__label">
                      {t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_DESCRIPTION')}
                      <span className="bombiix-input__required" aria-hidden="true">
                        *
                      </span>
                    </label>
                  )}
                  <div className="product-desc-check">
                    <NewCheckbox
                      size="sm"
                      id="descTags"
                      name="descTags"
                      aria-label={t('COMMON_LABEL.TAGS')}
                      checked={descTags}
                      onChange={(e: any) => handleDescAsTags(e.target.checked)}
                    />
                  </div>
                  {!isProductEdit ? (
                    <div className="cp-desc-typeahead">
                      <AsyncTypeahead
                        key={descTypeaheadKey}
                        id="asyncTypeheadWithDescription"
                        inputProps={{ maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH }}
                        promptText="No Product found"
                        labelKey="description"
                        placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.DESCRIPTION_PLACEHOLDER')}
                        onSearch={() => {}}
                        onChange={(selected: any) => onAutoSuggestion(selected)}
                        defaultInputValue={editedProductData?.description}
                        isLoading={suggestionLoader}
                        autoFocus={editedProductData?.description?.length > 0 && typeHeadOpen}
                        open={descProductList?.length > 0 && typeHeadOpen}
                        onInputChange={handleInputChange}
                        onBlur={handleBlur}
                        options={descProductList || []}
                        renderMenuItemChildren={(option: any) => (
                          <div className="d-flex" key={option._id}>
                            <div className="list-text">
                              {option?.number && shortenStringWithTooltipWithBold(option?.number?.toString() || '', `-${option?.description || ''}`, 40)}
                            </div>
                          </div>
                        )}
                      />
                      <span className="bombiix-input__counter cp-desc-typeahead__counter">
                        {editedProductData?.description?.length || 0}/{PRODUCT_DESCRIPTION_MAX_LENGTH}
                      </span>
                    </div>
                  ) : (
                    <NewInput
                      label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_DESCRIPTION')}
                      required
                      invalid={!!hasError('description')}
                      maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
                      value={editedProductData?.description || ''}
                      onChange={(e: any) => handleInput(e.target.value.trimStart(), 'description')}
                      endAdornment={
                        <span className="bombiix-input__counter">
                          {editedProductData?.description?.length || 0}/{PRODUCT_DESCRIPTION_MAX_LENGTH}
                        </span>
                      }
                      placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.DESCRIPTION_PLACEHOLDER')}
                    />
                  )}
                  <span className="error-message d-block">{hasError('description')}</span>
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND')}
                    required
                    invalid={!!hasError('brandId')}
                    options={brandOptions}
                    value={getSelectValue(brandOptions, editedProductData?.brandId)}
                    onChange={(e: any) => handleSelect(e, 'brandId')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND')}
                    isClearable
                  />
                  <span className="error-message d-block">{hasError('brandId')}</span>
                </div>
              </Col>
              {subBrandAccess && (
                <Col md="2">
                  <div className="common-react-select">
                    <NewSelectMultiSelect
                      label={t('SUB_BRAND.SUBBRAND')}
                      options={subBrandOptions?.filter((option: any) => option.isActive === true) || []}
                      value={subBrandOptions?.find((option: any) => option._id === editedProductData?.subBrandId && option.isActive === true) || null}
                      onChange={(e: any) => handleSelect(e, 'subBrandId')}
                      getOptionLabel={(option: any) => option?.name}
                      getOptionValue={(option: any) => option?._id}
                      placeholder="Select Sub Brand"
                      isClearable
                    />
                  </div>
                </Col>
              )}
              {licensorAccess && (
                <>
                  <Col md="2">
                    <div className="common-react-select">
                      <div className="bombiix-select__label d-flex align-items-center">
                        <div className="d-inline-flex align-items-center design-switch licensor-switch">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0 comment-switch-input"
                            id="isLicensedProductSwitch"
                            name="isLicensedProduct"
                            checked={editedProductData?.isLicensedProduct === true}
                            onChange={(e: any) => handleSelect(e.target.checked, 'isLicensedProduct')}
                          />
                          <label className="switch-text bombiix-select__label-text mb-0 ml-2" htmlFor="isLicensedProductSwitch">
                            {t('LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION.LICENSOR')}
                          </label>
                        </div>
                      </div>
                      <NewSelectMultiSelect
                        options={licensorList}
                        value={licensorList.find((option: any) => option._id === editedProductData?.licensorId) || null}
                        getOptionLabel={(option: any) => option.name}
                        getOptionValue={(option: any) => option._id}
                        onChange={(e: any) => handleSelect(e, 'licensorId')}
                        placeholder="Select Licensor"
                        isDisabled={!editedProductData?.isLicensedProduct}
                        isClearable
                      />
                    </div>
                  </Col>
                  <Col md="2">
                    <div className="common-react-select">
                      <NewSelectMultiSelect
                        label={t('LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION.LICENSE')}
                        options={licenseList}
                        value={licenseList.find((option: any) => option._id === editedProductData?.licenseId) || null}
                        getOptionLabel={(option: any) => option.name}
                        getOptionValue={(option: any) => option._id}
                        onChange={(e: any) => handleSelect(e, 'licenseId')}
                        placeholder="Select License"
                        isDisabled={!editedProductData?.isLicensedProduct}
                        isClearable
                      />
                    </div>
                  </Col>
                  <Col md="2">
                    <div className="common-react-select">
                      <NewSelectMultiSelect
                        label={t('LIKENESS_MASTER.ACTIVE_INACTIVE_CONFIRMATION.LIKENESS')}
                        options={likenessList}
                        value={likenessList.find((option: any) => option._id === editedProductData?.likenessId) || null}
                        onChange={(e: any) => handleSelect(e, 'likenessId')}
                        getOptionLabel={(option: any) => option.name}
                        getOptionValue={(option: any) => option._id}
                        placeholder={t('LIKENESS_MASTER.SELECT_LIKENESS')}
                        isDisabled={!editedProductData?.isLicensedProduct}
                        isClearable
                      />
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>

        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_CATEGORIZATION')}</Card.Header>
          <Card.Body>
            <Row>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE')}
                    required
                    invalid={!!hasError('supplierType')}
                    options={pcTypeList}
                    value={editedProductData?.supplierType}
                    onChange={(e: any) => handleSelect(e, 'supplierType')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE')}
                    isClearable
                  />
                  <span className="error-message d-block">{hasError('supplierType')}</span>
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGO0RY_1')}
                    required
                    invalid={!!hasError('catOneId')}
                    options={category1Options}
                    value={
                      getSelectValue(category1Options, editedProductData?.catOneId) ||
                      (editedProductData?.catOneId && editedProductData?.catOne ? { _id: editedProductData?.catOneId, name: editedProductData?.catOne } : null)
                    }
                    onChange={(e: any) => handleSelect(e, 'catOneId')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGO0RY_1')}
                    isClearable
                  />
                  <span className="error-message d-block">{hasError('catOneId')}</span>
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORIES_2')}
                    required
                    invalid={!!hasError('catTwoId')}
                    options={category2Options}
                    value={
                      getSelectValue(category2Options, editedProductData?.catTwoId) ||
                      (editedProductData?.catTwoId && editedProductData?.catTwo ? { _id: editedProductData?.catTwoId, name: editedProductData?.catTwo } : null)
                    }
                    onChange={(e: any) => handleSelect(e, 'catTwoId')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORIES_2')}
                    isClearable
                  />
                  <span className="error-message d-block">{hasError('catTwoId')}</span>
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORIES_3')}
                    options={category3Options?.length ? category3Options : category3List}
                    value={
                      getSelectValue(category3Options?.length ? category3Options : category3List, editedProductData?.catThreeId) ||
                      (editedProductData?.catThreeId && editedProductData?.catThree
                        ? { _id: editedProductData?.catThreeId, name: editedProductData?.catThree }
                        : null)
                    }
                    onChange={(e: any) => handleSelect(e, 'catThreeId')}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORIES_3')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    isClearable
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORY4')}
                    options={category4Options?.length ? category4Options : category4List}
                    value={
                      getSelectValue(category4Options?.length ? category4Options : category4List, editedProductData?.catFourId) ||
                      (editedProductData?.catFourId && editedProductData?.catFour
                        ? { _id: editedProductData?.catFourId, name: editedProductData?.catFour }
                        : null)
                    }
                    onChange={(e: any) => handleSelect(e, 'catFourId')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.CATEGORY4')}
                    isClearable
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.VARIABLES')}</Card.Header>
          <Card.Body>
            <Row>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SEASON')}
                    options={seasonList}
                    value={getSelectValue(seasonList, editedProductData?.seasonId)}
                    onChange={(e: any) => handleSelect(e, 'seasonId')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SEASON')}
                    isClearable
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.MONTH')}
                    options={[{ name: 'None', _id: null }, ...monthOptions]}
                    value={getSelectValue(monthOptions, editedProductData?.month)}
                    onChange={(e: any) => handleSelect(e, 'month')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.MONTH')}
                    isClearable
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.YEAR')}
                    options={[{ name: 'None', _id: null }, ...yearOptions]}
                    value={getSelectValue(yearOptions, editedProductData?.year)}
                    onChange={(e: any) => handleSelect(e, 'year')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.YEAR')}
                    isClearable
                    scrollToLabel={getCurrentYearLabel()}
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SIZE_RANGE_TITLE')}
                    isClearable
                    showSearch
                    searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_SIZE_RANGE')}
                    options={sizeRangeDropdownOptions}
                    value={
                      !isEmpty(editedProductData?.sizeRangeId)
                        ? sizeRangeDropdownOptions.find((val: any) => val.value === editedProductData?.sizeRangeId?.[0]) || null
                        : null
                    }
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_SIZE_RANGE')}
                    onChange={(selected: any) => handleSelect(selected ? [selected.value] : [], 'sizeRangeId')}
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SIZES')}
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SIZES')}
                    options={sizeRangeSizeDropdownOptions}
                    value={sizeRangeSizeDropdownOptions.filter(
                      (val: any) => !isEmpty(editedProductData?.sizeRangeSizes) && editedProductData?.sizeRangeSizes.includes(val.value),
                    )}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SIZES')}
                    onChange={(selected: any) =>
                      handleSelect(
                        (selected || []).map((val: any) => val.value),
                        'sizeRangeSizes',
                      )
                    }
                    isDisabled={!editedProductData?.sizeRangeId || editedProductData?.sizeRangeId?.length === 0}
                  />
                </div>
              </Col>
              {lengthEnabled && (
                <Col md="2">
                  <div className="common-react-select">
                    <NewSelectMultiSelect
                      label={lengthLabel}
                      isMulti
                      isClearable
                      showSearch
                      selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                      clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                      searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + lengthLabel}
                      options={lengthOptions}
                      value={lengthOptions.filter((val: any) => !isEmpty(editedProductData?.legLengths) && editedProductData?.legLengths.includes(val._id))}
                      placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + lengthLabel}
                      onChange={(selected: any) =>
                        handleSelect(
                          (selected || []).map((val: any) => val._id),
                          'legLengths',
                        )
                      }
                    />
                  </div>
                </Col>
              )}
              {/* Additional Variables: one option-dropdown per matched variable, shown right after Length */}
              {matchedAdditionalVariables.map((variable: AdditionalVariable) => {
                const variableId = String(variable._id);
                const variableName = variable.name ?? '';
                const options: AdditionalVariableOption[] = Array.isArray(variable.options) ? variable.options : [];
                const selectedOptionIds = getAdditionalVariableValue(variableId);
                return (
                  <Col md="2" key={variableId}>
                    <div className="common-react-select">
                      {/* Multi-select: every chosen option is a combination axis value, so picking
                          two options doubles the product-identifier rows, exactly like colourways. */}
                      <NewSelectMultiSelect
                        label={variableName}
                        options={options}
                        isMulti
                        value={options.filter((option: AdditionalVariableOption) => selectedOptionIds.includes(String(option._id)))}
                        getOptionLabel={(option: AdditionalVariableOption) => option.name ?? ''}
                        getOptionValue={(option: AdditionalVariableOption) => option._id}
                        isClearable
                        showSearch
                        selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                        clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                        placeholder={variableName}
                        onChange={(selected: AdditionalVariableOption | ReadonlyArray<AdditionalVariableOption> | null) =>
                          handleAdditionalVariableValueChange(
                            variableId,
                            (Array.isArray(selected) ? selected : []).map((option: AdditionalVariableOption) => String(option._id)),
                          )
                        }
                      />
                    </div>
                  </Col>
                );
              })}
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.COLOUR_WAYS')}
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.COLOUR_WAYS')}
                    options={colourOptions}
                    isOptionDisabled={(option: any) => inUseColourIds.has(String(option?._id))}
                    value={colourOptions.filter((val: any) => editedProductData?.colourLibraries?.includes(val._id))}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.COLOUR_WAYS')}
                    onChange={(selected: any) =>
                      handleSelect(
                        (selected || []).map((val: any) => val._id),
                        'colourLibraries',
                      )
                    }
                  />
                  <CustomTooltip placement="right" tooltipText={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.ADD')}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!colourwayModal) dispatch(fetchColorGroupLibrary({ loggedInUserId: userInfo.userId }));
                        setColourwayModal(!colourwayModal);
                      }}
                      className="bombiix-icon-container bombiix-icon-container--blue btn-absolute mr-0"
                      aria-label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.ADD')}
                    >
                      <i className="bombiix-icon-add" />
                    </button>
                  </CustomTooltip>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SUPPLY_CHAIN_DESTINATION')}</Card.Header>
          <Card.Body>
            <Row>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('product-information:PRODUCT_MODAL.SUPPLIER_LABEL')}
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={
                      t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_SUPPLIER')
                    }
                    options={supplierOptions}
                    isOptionDisabled={(option: SupplierOption) => inUseSupplierIds.has(String(option?._id))}
                    onLockedRemoval={showSupplierLockNotice}
                    value={supplierOptions.filter((val: any) => !isEmpty(editedProductData?.suppliers) && editedProductData?.suppliers?.includes(val.value))}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_SUPPLIER')}
                    onChange={(selected: any) =>
                      handleSelect(
                        (selected || []).map((val: any) => val._id),
                        'suppliers',
                      )
                    }
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('product-information:PRODUCT_MODAL.FACTORY_LABEL')}
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={
                      t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_FACTORY')
                    }
                    options={supplierFactoriesOptions}
                    isOptionDisabled={(option: FactoryOption) => inUseFactoryIds.has(String(option?._id))}
                    onLockedRemoval={showFactoryLockNotice}
                    value={supplierFactoriesOptions?.filter(
                      (val: any) => !isEmpty(editedProductData?.selectedSupplierFactories) && editedProductData?.selectedSupplierFactories.includes(val?.value),
                    )}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_FACTORY')}
                    onChange={(selected: any) =>
                      handleSelect(
                        (selected || []).map((val: any) => val._id),
                        'selectedSupplierFactories',
                      )
                    }
                  />
                </div>
              </Col>
              <Col className="add-form" md={3} lg={2}>
                <FormGroup>
                  <div className="common-react-select">
                    <NewSelectMultiSelect
                      label={t('BOM_TYPE_FORM.TRANSPORT')}
                      dppRequired
                      name="transport"
                      options={transportOption}
                      getOptionLabel={(option: any) => `${option.label}`}
                      getOptionValue={(option: any) => `${option.value}`}
                      value={transportOption.find((item: any) => item.value.toString() === editedProductData?.transport?.toString())}
                      onChange={(e: any) => handleSelect(e, 'transport')}
                      placeholder={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.SELECT_TRANSPORT')}
                      isClearable
                    />
                  </div>
                </FormGroup>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE_PROCESS')}
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_PRODUCT_TYPE_PROCESS')}
                    options={productTypeProcessOptions}
                    value={productTypeProcessOptions.filter(
                      (val: any) => !isEmpty(editedProductData?.productTypeProcesses) && editedProductData?.productTypeProcesses.includes(val._id),
                    )}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT_PRODUCT_TYPE_PROCESS')}
                    onChange={(selected: any) =>
                      handleSelect(
                        (selected || []).map((val: any) => val._id),
                        'productTypeProcesses',
                      )
                    }
                  />
                </div>
              </Col>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_SOLD_IN')}
                    dppRequired
                    isMulti
                    isClearable
                    showSearch
                    selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                    clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                    searchPlaceholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SEARCH_COUNTRIES')}
                    options={countryOptions}
                    value={selectedSoldCountryOptions}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_SOLD_IN')}
                    onChange={(selected: { value: string }[] | null) =>
                      handleSelect(
                        (selected || []).map((option) => option.value),
                        'soldCountries',
                      )
                    }
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {editedProductData?.supplierType?._id &&
          (dynamicFields?.some(
            (ele: any) => (ele.type === 'dropdown' || ele.type === 'radio-button' || ele.type === 'text' || ele.type === 'date-picker') && ele.isActive,
          ) ||
            multiDynamicAttribute?.some((ele: any) => ele.isActive && ele?.productType?.includes(editedProductData?.supplierType?._id))) && (
            <Card className="common-custom-card mb-3">
              <Card.Header>{t('CUSTOM_ATTRIBUTES.CUSTOM_ATTRIBUTES_TITLE') || 'Custom Attributes'}</Card.Header>
              <Card.Body>
                <Row>
                  {dynamicFields?.map((ele: any) => {
                    const id = ele?._id;
                    const selectedItem = editedProductData?.dynamicColumn?.find((eleDyna: any) => getEntryAttrId(eleDyna) === ele?._id);
                    /*
                     * Dropdown and radio-button are both single-select option types (mirrors the
                     * table view's `isOption`), so render both as a single-select here.
                     */
                    if (ele.type === 'radio-button' && ele.isActive) {
                      /*
                       * A radio-button attribute renders as radios, matching ProductModal.js (~4298)
                       * and the attribute type the user picked in System Masters. It had been folded
                       * into the dropdown branch below during the TSX port, which silently turned
                       * every radio attribute into a single-select. Inactive options stay hidden
                       * unless one is the currently-stored value, so an existing selection is never
                       * dropped just because its option was later deactivated.
                       */
                      const selectedOptionId = selectedItem?.selectedOpt?._id ? String(selectedItem.selectedOpt._id) : null;
                      const radioOptions = (ele?.values || []).filter((option: any) => option.isActive || String(option._id) === selectedOptionId);
                      return (
                        <Col md="2" key={id}>
                          <div className="common-input-control">
                            <span className="bombiix-input__label">{ele?.keyName}</span>
                            <div className="d-flex flex-wrap custom-attr-radio-list">
                              {radioOptions.map((option: any) => (
                                <NewRadio
                                  key={option?._id}
                                  className="mr-3 mb-2"
                                  id={`radio-${ele?._id}-${option?._id}`}
                                  name={`radio-${ele?._id}`}
                                  label={option?.keyName}
                                  value={String(option?._id)}
                                  checked={selectedOptionId === String(option?._id)}
                                  onChange={() => handleSelectDynamic(option, 'selectedOpt', ele?._id)}
                                />
                              ))}
                            </div>
                          </div>
                        </Col>
                      );
                    }
                    if (ele.type === 'dropdown' && ele.isActive) {
                      return (
                        <Col md="2" key={id}>
                          <div className="common-react-select">
                            <NewSelectMultiSelect
                              label={ele?.keyName}
                              options={(ele?.values || []).filter((option: any) => option.isActive)}
                              value={selectedItem?.selectedOpt || null}
                              onChange={(e: any) => handleSelectDynamic(e, 'selectedOpt', ele?._id)}
                              getOptionLabel={(option: any) => option?.keyName}
                              getOptionValue={(option: any) => option?._id}
                              isClearable
                              placeholder={ele?.keyName}
                            />
                          </div>
                          <CustomTooltip placement="right" tooltipText={t('COMMON_LABEL.ADD')}>
                            <button
                              type="button"
                              aria-label="add"
                              onClick={() => {
                                addCustomAttributeType(ele?._id);
                              }}
                              className="bombiix-icon-container bombiix-icon-container--blue multi-custom-dropdown-opt mr-0"
                            >
                              <i className="bombiix-icon-add" />
                            </button>
                          </CustomTooltip>
                        </Col>
                      );
                    }
                    return null;
                  })}
                  {dynamicFields?.map((ele: any) => {
                    const idText = ele?._id;
                    const maxlen = ele.textFieldConfig?.charLimit;
                    const isExceedable = ele.textFieldConfig?.isExceedable;
                    const isHightlight = ele.textFieldConfig?.isHighlightable;
                    const shouldHighlight = isExceedable && isHightlight && ele?.valueText?.length > ele.textFieldConfig?.charLimit;
                    const selectedItem = editedProductData?.dynamicColumn?.find((eleDyna: any) => getEntryAttrId(eleDyna) === ele?._id);
                    const currentLen = (selectedItem?.valueText || '').length;
                    const overLimit = !!maxlen && currentLen > maxlen;
                    if (ele.type === 'text' && ele.isActive) {
                      return (
                        <Col key={idText} md="2">
                          <div className="common-input-control">
                            <NewInput
                              label={ele.keyName}
                              maxLength={isExceedable ? undefined : maxlen}
                              disabled={!ele?.textFieldConfig?.isEditable}
                              className={shouldHighlight || overLimit ? 'text-danger' : ''}
                              value={selectedItem ? selectedItem?.valueText : ''}
                              onChange={(e: any) => handleSelectDynamic(e.target.value, 'valueText', ele?._id)}
                            />
                            {overLimit && <small className="text-danger d-block mt-1">{t('CUSTOM_ATTRIBUTES.CHAR_LIMIT_EXCEEDED', { limit: maxlen })}</small>}
                          </div>
                        </Col>
                      );
                    }
                    return null;
                  })}
                  {dynamicFields?.map((ele: any) => {
                    const idDate = ele?._id;
                    const selectedItem = editedProductData?.dynamicColumn?.find((eleDyna: any) => getEntryAttrId(eleDyna) === ele?._id);
                    if (ele.type === 'date-picker' && ele.isActive) {
                      /*
                       * Date custom attribute stores its value as an ISO day string (YYYY-MM-DD)
                       * under valueText — same slot as text — parsed to a Date for the picker.
                       */
                      const rawDate = selectedItem?.valueText;
                      const dateValue = rawDate ? new Date(rawDate) : null;
                      return (
                        <Col key={idDate} md="2">
                          <div className="common-input-control">
                            <div className="bombiix-input-wrap">
                              <span className="bombiix-input__label">{ele?.keyName}</span>
                              <NewDatePicker
                                value={dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue : null}
                                /* Disable ONLY when the master explicitly marks the field non-editable.
                                   `!isEditable` disabled the field whenever `datePickerConfig` was absent — and it is
                                   ALWAYS absent: model/masters/CustomAttributeMaster.js declares textFieldConfig but
                                   no datePickerConfig, so Mongoose's strict mode drops it and no date attribute can
                                   ever carry one (verified: 0 of 0 in the dev DB). Every date custom attribute was
                                   therefore permanently read-only. Mirrors PlaceholderExpandRows, which already
                                   treats a missing config as "unconfigured" rather than "not editable". */
                                disabled={ele?.datePickerConfig?.isEditable === false}
                                placeholder={ele?.keyName}
                                autoApply
                                onChange={(date: Date | null) => {
                                  const isoDay = date
                                    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                                    : '';
                                  handleSelectDynamic(isoDay, 'valueText', ele?._id);
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      );
                    }
                    return null;
                  })}
                  {multiDynamicAttribute?.map((ele: any) => {
                    const id = ele._id;
                    if (ele.isActive && ele?.productType?.includes(editedProductData?.supplierType?._id)) {
                      const filteredAttributes = editedProductData?.multiCustomAttribute?.filter((attribute: any) => attribute.colId === ele._id);
                      const filteredValues = ele.values.filter((item: any) => filteredAttributes?.some((attribute: any) => attribute.valId.includes(item._id)));
                      return (
                        <Col md="2" key={id}>
                          <div className="common-react-select">
                            <NewSelectMultiSelect
                              label={ele?.keyName}
                              isMulti
                              isClearable
                              showSearch
                              selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                              clearAllLabel={t('COMMON_LABEL.CLEAR_ALL')}
                              searchPlaceholder={`${t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT')} ${ele?.keyName}`}
                              options={ele.values.map((item: any) => ({ label: item.keyName, value: item._id }))}
                              value={filteredValues.map((item: any) => ({ label: item.keyName, value: item._id }))}
                              placeholder={`${t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT')} ${ele?.keyName}`}
                              onChange={(selected: any) =>
                                handleSelect(
                                  (selected || []).map((val: any) => val.value),
                                  'multiCustomAttribute',
                                  ele?._id,
                                )
                              }
                            />
                          </div>
                          <CustomTooltip placement="right" tooltipText={t('COMMON_LABEL.ADD')}>
                            <button
                              type="button"
                              aria-label="add"
                              onClick={() => {
                                addComponentType(ele?._id);
                              }}
                              className="bombiix-icon-container bombiix-icon-container--blue multi-custom-dropdown-opt mr-0"
                            >
                              <i className="bombiix-icon-add" />
                            </button>
                          </CustomTooltip>
                        </Col>
                      );
                    }
                    return null;
                  })}
                </Row>
              </Card.Body>
            </Card>
          )}

        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.STATUSES')}</Card.Header>
          <Card.Body>
            <Row>
              <Col md="2">
                <div className="common-react-select">
                  <NewSelectMultiSelect
                    label={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.STATUS')}
                    options={productStatusList}
                    value={editedProductData?.status}
                    onChange={(e: any) => handleSelect(e, 'status')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option._id}
                    placeholder={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.SELECT') + t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.STATUS')}
                    isClearable
                  />
                </div>
              </Col>
            </Row>
            <ProductIdentifier
              editedProductData={editedProductData}
              showLabel={showLabel}
              sizeOptions={sizeOptions}
              lengthOptions={lengthOptions}
              colorOptions={colorOptionForProductIdentifier}
              refs={productIdentiRef}
            />
          </Card.Body>
        </Card>

        <Card className="common-custom-card mb-3">
          <Card.Header>{t('COMMON_LABEL.TAGS')}</Card.Header>
          <Card.Body>
            <BombiixTagsInput
              tags={tags}
              suggestions={tagsOptions}
              onDelete={onDelete}
              onAddition={onAddition}
              onFocus={getOtherTags}
              allowNew
              delimiters={[',', 'Enter', 'Tab']}
              placeholderText={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.ADD_TAG')}
            />
          </Card.Body>
        </Card>
      </div>

      {colourwayModal && (
        <CommonColorWayAdd onOpen={colourwayModal} toogle={closeColourModal} imageUpload={toogleColorImageUploder} hideDocument extraClass="cp-bordered" />
      )}

      {/* On EDIT the modal searches the assets already attached to this product (its default,
          driven by productId); on CREATE there is no product id yet, so it falls back to the
          brand / licensing context search. Mirrors ProductModal.js. */}
      {isImageUploadModalOpen && (
        <ImageUploadModal
          show={isImageUploadModalOpen}
          productId={editedProductData?._id || devOrderId || null}
          {...(isProductEdit ? {} : { fetchImages: fetchProductImages })}
          onClose={closeImageUpload}
          onSave={handleNewImageUploadSave}
          allowedImageExtensions={IMAGE_PICKER_EXTENSIONS}
          showAddToDesign
          showAddToAssetLibrary
          existingImage={croppedImage && croppedImage !== noImage ? (croppedImage as string) : null}
        />
      )}

      {state?.showMultiAttributePopup && (
        <AddMultiDropOpt
          onOpen={state?.showMultiAttributePopup}
          onSubmit={(data: any, newOptions: any[]) => {
            saveMultiCustomAttribute(data, newOptions);
          }}
          bomTypeId={state?.multiDynamicId}
          multiDynamicAttribute={multiDynamicAttribute}
          loggedInUserId={userInfo?.userId}
          loading={false}
          toggle={() => toggelMultiCustomAttribute()}
          label={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_TYPE')}
          extraClass="cp-bordered"
        />
      )}
      {state?.showCustomAttributeOpt && (
        <AddCustomDropOpt
          onOpen={state?.showCustomAttributeOpt}
          onSubmit={(data: any, newcustomOptions: any[]) => {
            saveCustomAttribute(data, newcustomOptions);
          }}
          bomTypeId={state?.customAttributeId}
          multiDynamicAttribute={dynamicFields?.filter((e: any) => e.type === 'dropdown' || e.type === 'radio-button')}
          loggedInUserId={userInfo?.userId}
          loading={false}
          toggle={() => toggelCustomAttribute()}
          label={t('BOM_LIBRARY.ADD_EDIT_BOM_LIBRARY.COMPONENT_TYPE')}
          extraClass="cp-bordered"
        />
      )}
      {isProductComponentModal && (
        <ProductComponentModal show={isProductComponentModal} handleClose={() => setIsProductComponentModal(false)} onSaveClick={onProductComponentSave} />
      )}
      {damConfirmModal && (
        <ConfirmButtonPopup
          onOpen={damConfirmModal}
          onConfirm={() => deleteDamDataAfterProductTypeAndBrandChange()}
          title={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_CHANGE')}
          message={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_MESSAGE')}
          secondMessage={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_SECOND_MESSAGE')}
          toggle={() => closeDamCheckModal()}
          requireTypedConfirm
        />
      )}
      {isProductEdit && showFactoryModal && (
        <ConfirmationPopup
          onOpen={showFactoryModal}
          onConfirm={confirmFactoryChange}
          onCancel={cancelFactoryChange}
          title={t('EDIT_FIELD_MODAL.REMOVE_FACTORY')}
          message={t('EDIT_FIELD_MODAL.FIRST_MESSAGE')}
          secondMessage={t('EDIT_FIELD_MODAL.SECOND_MESSAGE')}
          toggle={cancelFactoryChange}
        />
      )}
      {showBrandSubBrandModal && (
        <ConfirmButtonPopup
          onOpen={showBrandSubBrandModal}
          onConfirm={() => confirmBrandSubBrandChange()}
          title={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_CHANGE')}
          message={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_MESSAGE')}
          secondMessage={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.BRAND_SUB_BRAND_SECOND_MESSAGE')}
          toggle={() => cancelBrandSubBrandChange()}
          requireTypedConfirm
        />
      )}
      {showGradingScopeModal && (
        <ConfirmButtonPopup
          onOpen={showGradingScopeModal}
          onConfirm={() => confirmGradingScopeChange()}
          title={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_TITLE')}
          message={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_MESSAGE')}
          secondMessage={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.GRADING_SCOPE_CHANGE_SECOND_MESSAGE')}
          toggle={() => cancelGradingScopeChange()}
          requireTypedConfirm
        />
      )}
      {pendingProductTypeChange && (
        <ConfirmButtonPopup
          onOpen={Boolean(pendingProductTypeChange)}
          onConfirm={() => confirmProductTypeChange()}
          title={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE_CHANGE')}
          message={t('COMPONENT.PRODUCT_INFORMATION.PRODUCT_MODAL.PRODUCT_TYPE_CHANGE_MESSAGE')}
          secondMessage={<ProductTypeChangeWarning />}
          toggle={cancelProductTypeChange}
          requireTypedConfirm
          className="product-type-change-modal"
        />
      )}
      {showFactoryInUseModal && (
        <BombiixModal show={showFactoryInUseModal} title={t('product-information:FACTORY_LOCK.TITLE')} onClose={dismissFactoryInUseModal} hideSave size="md">
          <p className="mb-0 text-center">
            {t('product-information:FACTORY_LOCK.MESSAGE', { factoryNames: resolveInUseFactoryNames(blockedInUseFactoryIds, supplierFactories) })}
          </p>
        </BombiixModal>
      )}
      {Boolean(blockedInUseSupplierNames) && (
        <BombiixModal
          show={Boolean(blockedInUseSupplierNames)}
          title={t('product-information:SUPPLIER_LOCK.TITLE')}
          onClose={() => setBlockedInUseSupplierNames('')}
          hideSave
          size="md"
        >
          <p className="mb-0 text-center">{t('product-information:SUPPLIER_LOCK.MESSAGE', { supplierNames: blockedInUseSupplierNames })}</p>
        </BombiixModal>
      )}
      {pendingIdentifierChange && (
        <ConfirmButtonPopup
          onOpen={Boolean(pendingIdentifierChange)}
          onConfirm={() => confirmIdentifierChange()}
          title={t('product-information:PRODUCT_IDENTIFIER_CHANGE.TITLE')}
          message={t('product-information:PRODUCT_IDENTIFIER_CHANGE.MESSAGE', { variableOneName: lengthLabel })}
          secondMessage={t('product-information:PRODUCT_IDENTIFIER_CHANGE.SECOND_MESSAGE')}
          toggle={() => cancelIdentifierChange()}
          onCancel={() => cancelIdentifierChange()}
          requireTypedConfirm
        />
      )}
    </div>
  );
}

export default CreateProduct;

/** The four category levels whose type-based options the page loads once on open. */
const CATEGORY_TYPES = [1, 2, 3, 4];
